import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/slices/authSlice';
import api from '../../services/api';
import usePermissions from '../../hooks/usePermissions';
import useNavigationGating, { buildChildKeys } from '../../hooks/useNavigationGating';
import OfflineUploadStatus from '../pwa/OfflineUploadStatus';
import HelpAssistantWidget from '../help-assistant/HelpAssistantWidget';
import HotNewsBanner from '../knowledge/HotNewsBanner';
import ScreenNotesButton from '../knowledge/ScreenNotesButton';
import GlobalSearchBar from '../search/SearchBar';
import { recordRecentPage } from '../../utils/recentPages';
import SearchSpotlightModal from '../search/SearchSpotlightModal';
import CompanySwitcher from './CompanySwitcher';
import { useActiveCompany, loadActiveCompany, clearActiveCompany } from '../../services/activeCompany';
import {
  fetchUnreadNotificationCount,
  selectUnreadCount,
} from '../../redux/slices/workflowApprovalSlice';
import { styled } from '@mui/material/styles';
import {
  Box,
  CircularProgress,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Badge,
  InputBase,
  Avatar,
  Collapse,
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer as MuiDrawer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HotelIcon from '@mui/icons-material/Hotel';
import PrintIcon from '@mui/icons-material/Print';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Divider from '@mui/material/Divider';

// Icons for apps
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalculateIcon from '@mui/icons-material/Calculate';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import TaskIcon from '@mui/icons-material/Task';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import StorageIcon from '@mui/icons-material/Storage';
import PublicIcon from '@mui/icons-material/Public';
import MapIcon from '@mui/icons-material/Map';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import StraightenIcon from '@mui/icons-material/Straighten';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import ApprovalIcon from '@mui/icons-material/Approval';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CompareIcon from '@mui/icons-material/Compare';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InsightsIcon from '@mui/icons-material/Insights';
import TerminalIcon from '@mui/icons-material/Terminal';
import TableChartIcon from '@mui/icons-material/TableChart';
import BackupIcon from '@mui/icons-material/Backup';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockClockIcon from '@mui/icons-material/LockClock';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FactoryIcon from '@mui/icons-material/Factory';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import BadgeIcon from '@mui/icons-material/Badge';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
// AssignmentTurnedInIcon is already imported further down this file — reusing
// it rather than declaring a second binding.
import WorkIcon from '@mui/icons-material/Work';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import GridViewIcon from '@mui/icons-material/GridView';
import GavelIcon from '@mui/icons-material/Gavel';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DrawIcon from '@mui/icons-material/Draw';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import HandshakeIcon from '@mui/icons-material/Handshake';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HubIcon from '@mui/icons-material/Hub';
import BuildIcon from '@mui/icons-material/Build';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import FlagIcon from '@mui/icons-material/Flag';
import TuneIcon from '@mui/icons-material/Tune';
import GridOnIcon from '@mui/icons-material/GridOn';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CategoryIcon from '@mui/icons-material/Category';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import PaymentsIcon from '@mui/icons-material/Payments';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ScaleIcon from '@mui/icons-material/Scale';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BalanceIcon from '@mui/icons-material/Balance';
import PollIcon from '@mui/icons-material/Poll';
import GroupsIcon from '@mui/icons-material/Groups';
import BugReportIcon from '@mui/icons-material/BugReport';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import SpeedIcon from '@mui/icons-material/Speed';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import TimerIcon from '@mui/icons-material/Timer';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LayersIcon from '@mui/icons-material/Layers';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FolderIcon from '@mui/icons-material/Folder';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import ConstructionIcon from '@mui/icons-material/Construction';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ArticleIcon from '@mui/icons-material/Article';
import ShareIcon from '@mui/icons-material/Share';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PolicyIcon from '@mui/icons-material/Policy';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NotificationDropdown from '../workflow/NotificationDropdown';
import LicenseGraceBanner from '../licensing/LicenseGraceBanner';
import useWebPush from '../../hooks/useWebPush';
import LanguageSwitcher from '../common/LanguageSwitcher';

const SIDEBAR_WIDTH = 250;
const SIDEBAR_COLLAPSED = 60;
const HEADER_HEIGHT = 64;

// Sidebar navigation structure grouped by section
const sidebarSections = [
  {
    id: 'overview',
    items: [
      {
        key: 'home',
        label: 'Overview',
        icon: <DashboardIcon />,
        path: '/',
      },
      {
        key: 'my-workspace',
        label: 'My Workspace',
        icon: <PeopleIcon />,
        path: '/ess',
        permission: 'ess.use_portal',
        submenus: [
          { text: 'My Dashboard', icon: <DashboardIcon />, path: '/ess' },
          { text: 'My Profile', icon: <PeopleIcon />, path: '/ess/profile' },
          { text: 'Leave', icon: <EventNoteIcon />, path: '/ess/leave' },
          // Employee Relations self-service (DEV-10 §5.12): my own notices and
          // my own grievances, scoped to the caller's employee record on the
          // server — same ESS gate as the rest.
          { text: 'My Notices', icon: <GavelIcon />, path: '/ess/notices' },
          { text: 'Grievances & Suggestions', icon: <ReportProblemIcon />, path: '/ess/complaints' },
          // Objects on Loan self-service (DEV-11 §5.11): my own company
          // objects — acknowledge, dispute, report loss / damage, the exit list.
          { text: 'My Objects', icon: <InventoryIcon />, path: '/ess/my-objects' },
          { text: 'My Hostel', icon: <HotelIcon />, path: '/ess/my-hostel' },
          // Employee Loans self-service (DEV-03 §5.14): apply, track, statement.
          { text: 'My Loans', icon: <CurrencyRupeeIcon />, path: '/ess/my-loans' },
          { text: 'My Referrals', icon: <PeopleIcon />, path: '/ess/my-referrals' },
          // Statutory Bonus self-service (DEV-02 §5.15): bonus slips + acknowledgement.
          { text: 'My Bonus', icon: <CurrencyRupeeIcon />, path: '/ess/my-bonus' },
          // Shift rotation & rostering, self-service. Both are scoped to the
          // caller's own employee record ON THE SERVER, so they need no
          // codename beyond the existing ESS gate — and "My Roster" shows
          // PUBLISHED rosters only, never a planner's draft.
          { text: 'My Roster', icon: <CalendarMonthIcon />, path: '/ess/roster' },
          { text: 'My Attendance', icon: <ScheduleIcon />, path: '/ess/attendance' },
          // Read-only holiday calendar. Same ESS gate as the rest: the
          // server scopes it to the caller's own plant, so it needs no
          // codename of its own.
          { text: 'Holidays', icon: <CalendarMonthIcon />, path: '/ess/holidays' },
          { text: 'Payslips', icon: <PaymentsIcon />, path: '/ess/payslips' },
          { text: 'Expenses', icon: <ReceiptLongIcon />, path: '/ess/expenses' },
          { text: 'Directory', icon: <PeopleIcon />, path: '/ess/directory' },
          { text: 'Leave Approvals', icon: <VerifiedUserIcon />, path: '/ess/leave/approvals', permission: 'ess.approve_team' },
          { text: 'Expense Approvals', icon: <VerifiedUserIcon />, path: '/ess/expenses/approvals', permission: 'ess.approve_team' },
          { text: 'Profile Approvals', icon: <VerifiedUserIcon />, path: '/ess/hr/profile-requests', permission: 'ess.hr_review' },
        ],
      },
    ],
  },
  {
    id: 'operational',
    header: 'OPERATIONAL',
    items: [
      {
        key: 'crm',
        label: 'CRM',
        icon: <PeopleIcon />,
        path: '/crm',
        permission: 'crm.view_lead',
        submenus: [
          { text: 'Leads', icon: <PeopleIcon />, path: '/crm/leads' },
          { text: 'Opportunities', icon: <BusinessIcon />, path: '/crm/opportunities' },
          { text: 'Customers', icon: <PeopleIcon />, path: '/crm/customers' },
          { text: 'Quotations', icon: <AssignmentIcon />, path: '/crm/quotations' },
          { text: 'Sales Orders', icon: <ReceiptIcon />, path: '/crm/sales-orders' },
          { text: 'SO Line Items', icon: <ReceiptIcon />, path: '/crm/sales-orders/line-items', permission: 'crm.view_sales_order' },
          { text: 'Order Book', icon: <ReceiptIcon />, path: '/crm/order-book', permission: 'crm.view_sales_order' },
          { text: 'SO Inventory', icon: <Inventory2Icon />, path: '/crm/so-inventory', permission: 'mm.view_inventory' },
          { text: 'Photo Summary', icon: <ReceiptIcon />, path: '/crm/sales-orders/summary-photos', permission: 'crm.view_sales_order' },
          { text: 'Merchandising', icon: <BarChartIcon />, path: '/crm/merchandising', permission: 'crm.view_merch_dashboard' },
          { text: 'TNA Templates', icon: <AssignmentIcon />, path: '/crm/merchandising/tna-templates', permission: 'crm.view_order_tna' },
          { text: 'PP Meetings', icon: <AssignmentIcon />, path: '/crm/merchandising/pp-meetings', permission: 'crm.view_order_tna' },
          { text: 'TNA Adherence', icon: <BarChartIcon />, path: '/crm/merchandising/tna-adherence', permission: 'crm.view_order_tna' },
          { text: 'TNA & PP Setup', icon: <AssignmentIcon />, path: '/crm/merchandising/tna-setup', permission: 'crm.view_order_tna' },
          { text: 'Order Pipeline', icon: <BarChartIcon />, path: '/crm/order-pipeline', permission: 'crm.view_order_pipeline' },
          { text: 'Programme Pipeline', icon: <BarChartIcon />, path: '/crm/order-pipeline/programme', permission: 'crm.view_all_order_pipelines' },
          { text: 'Order Cost Closures', icon: <AssignmentIcon />, path: '/crm/order-closures', permissionAny: ['crm.prepare_order_closure', 'crm.approve_order_closure', 'crm.view_so_pnl'] },
          { text: 'Pipeline & Closure Setup', icon: <AssignmentIcon />, path: '/crm/order-pipeline/setup', permission: 'crm.manage_pipeline_config' },
          { text: 'SO Status (MTO)', icon: <BarChartIcon />, path: '/crm/so-status' },
          { text: 'SO Status (Detailed)', icon: <BarChartIcon />, path: '/crm/so-status/detailed', permission: 'crm.view_so_status' },
          { text: 'SO Journey', icon: <BarChartIcon />, path: '/crm/so-status/journey' },
          { text: 'SO Reconciliation', icon: <BarChartIcon />, path: '/crm/so-status/reconciliation' },
          { text: 'SO P&L (Est vs Act)', icon: <BarChartIcon />, path: '/crm/so-status/pnl' },
          { text: 'Sales Dashboard', icon: <BarChartIcon />, path: '/crm/sales-dashboard' },
          { text: 'Delivery', icon: <LocalShippingIcon />, path: '/crm/delivery' },
          { text: 'Invoices', icon: <InventoryIcon />, path: '/crm/invoices' },
          { text: 'Payments', icon: <PaymentIcon />, path: '/crm/payments' },
          { text: 'Sales Returns', icon: <AssignmentReturnIcon />, path: '/crm/returns', permission: 'crm.view_return' },
          { text: 'Free of Cost (FOC)', icon: <LocalShippingIcon />, path: '/foc', permission: 'foc.view_foc' },
          { text: 'Credit Management', icon: <PaymentIcon />, path: '/credit-management', permission: 'credit.view_credit' },
          { text: 'Pricing', icon: <PaymentIcon />, path: '/crm/pricing', permission: 'sdp.view_pricing' },
          { text: 'Customer Portal', icon: <PeopleIcon />, path: '/admin/customer-portal', permission: 'cportal.view_portal' },
          { text: 'Business Partners', icon: <BusinessIcon />, path: '/partners/business-partners', permission: 'bp.view_partner' },
          { text: 'Partner Profitability', icon: <BarChartIcon />, path: '/partners/profitability', permission: 'bp.view_exposure' },
          { text: 'Partner Set-off Runs', icon: <ReceiptIcon />, path: '/fi-netting/setoff-runs', permission: 'netting.view_setoff' },
          { text: 'Job Work Orders', icon: <ReceiptIcon />, path: '/crm/job-work-orders', permission: 'jw.view_joborder' },
          { text: 'JW Inward Challans', icon: <LocalShippingIcon />, path: '/crm/job-work/inward-challans', permission: 'jw.view_joborder' },
          { text: 'JW Challan Ageing', icon: <ReceiptIcon />, path: '/crm/job-work/challan-ageing', permission: 'jw.view_reports' },
        ],
      },
      {
        key: 'dispute-management',
        label: 'Disputes',
        icon: <GavelIcon />,
        path: '/dispute-management',
        permission: 'dispute.view_dispute',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/dispute-management' },
          { text: 'All Disputes', icon: <GavelIcon />, path: '/dispute-management/list' },
          { text: 'Raise Dispute', icon: <AddIcon />, path: '/dispute-management/new' },
        ],
      },
      {
        key: 'materials',
        label: 'Materials Mgt',
        icon: <InventoryIcon />,
        path: '/materials',
        permission: 'mm.view_material',
        submenus: [
          { text: 'Material Master', icon: <InventoryIcon />, path: '/materials/material-master' },
          { text: 'Service Master', icon: <BuildIcon />, path: '/materials/service-master' },
          { text: 'Material Variants', icon: <CategoryIcon />, path: '/materials/material-variants', permission: 'mm.view_material' },
          { text: 'Variant Attributes', icon: <CategoryIcon />, path: '/materials/variant-attributes', permission: 'mm.view_material' },
          { text: 'Product Specs & Docs', icon: <AssignmentIcon />, path: '/materials/product-specifications' },
          { text: 'Inventory', icon: <InventoryIcon />, path: '/materials/inventory' },
          { text: 'Inventory Dashboard', icon: <InventoryIcon />, path: '/materials/inventory-dashboard', permission: 'mm.view_inventory' },
          { text: 'Stock Abstract', icon: <ListAltIcon />, path: '/materials/stock-abstract', permission: 'mm.view_inventory' },
          { text: 'Stock at Vendors', icon: <AssignmentIcon />, path: '/materials/vendor-stock', permission: 'mm.view_vendor_stock' },
          { text: 'Customer-owned Stock', icon: <InventoryIcon />, path: '/materials/customer-owned-stock', permission: 'jw.view_reports' },
          { text: 'Rejected & Surplus Goods', icon: <InventoryIcon />, path: '/materials/rejections', permission: 'mm.view_rejection' },
          { text: 'Variant Value Maps', icon: <CategoryIcon />, path: '/materials/variant-value-maps', permission: 'pp.view_variant_map' },
          { text: 'Supplier Material Refs', icon: <AssignmentIcon />, path: '/materials/supplier-material-references', permission: 'mm.view_material' },
          { text: 'Variant Reclassification', icon: <CategoryIcon />, path: '/materials/variant-reclassification', permission: 'mm.reclassify_variant_stock' },
          { text: 'Moisture Weight Adj', icon: <ScaleIcon />, path: '/materials/weight-adjustments', permission: 'mm.view_weight_adjustment' },
          { text: 'Mobile Warehouse', icon: <InventoryIcon />, path: '/wms', permission: 'wms.use_mobile' },
          { text: 'Landed Cost (Imports)', icon: <LocalShippingIcon />, path: '/materials/landed-cost', permission: 'lc.view_landed_cost' },
          { text: 'Serial Numbers', icon: <InventoryIcon />, path: '/materials/serial-numbers', permission: 'sn.view_serial' },
          // FS-P2P-04: fabric rolls - receipt capture, QR labels, scan moves, grade from inspection
          { text: 'Fabric Rolls', icon: <InventoryIcon />, path: '/materials/fabric-rolls', permission: 'mm.view_rolls' },
          { text: 'Material Batches', icon: <InventoryIcon />, path: '/materials/material-batches' },
          { text: 'Goods Receipt', icon: <LocalShippingIcon />, path: '/materials/goods-receipt', permission: 'srm.view_purchase_order' },
          { text: 'GR from Delivery Note (AI)', icon: <LocalShippingIcon />, path: '/materials/gr-capture', permission: 'grc.view_capture' },
          { text: 'Goods Issue Production', icon: <LocalShippingIcon />, path: '/materials/goods-issue-production', permissionAny: ['mm.view_goods_issue_prod', 'mm.issue_goods_production'] },
          { text: 'Goods Issue Sales Order', icon: <LocalShippingIcon />, path: '/materials/goods-issue-sales-order', permissionAny: ['mm.view_goods_issue_so', 'mm.issue_goods_sales_order'] },
          { text: 'Goods Issue Cost Centre', icon: <LocalShippingIcon />, path: '/materials/goods-issue-cost-center', permissionAny: ['mm.view_goods_issue_cc', 'mm.issue_goods_cost_center'] },
          { text: 'Goods Receipt Production', icon: <LocalShippingIcon />, path: '/materials/goods-receipt-production', permissionAny: ['mm.view_goods_receipt_prod', 'mm.receive_goods_production'] },
          { text: 'Transfer', icon: <SwapHorizIcon />, path: '/materials/transfer', permission: 'mm.view_inventory' },
          { text: 'Stock Transport Orders', icon: <LocalShippingIcon />, path: '/materials/stock-transport', permissionAny: ['sto.view_order', 'sto.add_order', 'sto.change_order', 'sto.approve_order', 'sto.issue_goods', 'sto.receive_goods', 'sto.reverse_movement', 'sto.write_off_transit', 'sto.short_close_order', 'sto.manage_config', 'sto.view_reports'] },
          { text: 'STO Inbound', icon: <LocalShippingIcon />, path: '/materials/stock-transport/inbound', permissionAny: ['sto.receive_goods', 'sto.write_off_transit', 'sto.view_order'] },
          { text: 'Stock in Transit', icon: <ListAltIcon />, path: '/materials/stock-transport/in-transit', permissionAny: ['sto.view_reports', 'sto.view_order'] },
          { text: 'STO Replenishment', icon: <LocalShippingIcon />, path: '/materials/stock-transport/replenishment', permissionAny: ['sto.add_order', 'sto.view_reports', 'sto.manage_config'] },
          { text: 'STO Mobile (Pick / Receive)', icon: <LocalShippingIcon />, path: '/materials/stock-transport/mobile', permissionAny: ['sto.issue_goods', 'sto.receive_goods'] },
          { text: 'STO Settings', icon: <SwapHorizIcon />, path: '/materials/stock-transport/settings', permission: 'sto.manage_config' },
          { text: 'Material Documents', icon: <ListAltIcon />, path: '/materials/material-documents' },
          { text: 'MD Line Items', icon: <ReceiptIcon />, path: '/materials/material-documents/line-items', permission: 'mm.view_inventory' },
          { text: 'Handling Units', icon: <ViewModuleIcon />, path: '/materials/handling-units' },
          { text: 'Stock Audit', icon: <FactCheckIcon />, path: '/stock-audit', permission: 'mm.view_inventory' },
        ],
      },
      {
        key: 'srm',
        label: 'Supplier Mgt',
        icon: <LocalShippingIcon />,
        path: '/srm',
        permission: 'srm.view_supplier',
        submenus: [
          { text: 'Supplier Discovery', icon: <SearchIcon />, path: '/supplier-discovery' },
          { text: 'Supplier Onboarding', icon: <AssignmentIcon />, path: '/supplier-onboarding' },
          { text: 'Onboarding Templates', icon: <AssignmentIcon />, path: '/onboarding-templates' },
          { text: 'Supplier Messages', icon: <ChatIcon />, path: '/supplier-messages' },
          { text: 'Supplier Documents', icon: <AssignmentIcon />, path: '/supplier-documents' },
          { text: 'Incoming Shipments', icon: <LocalShippingIcon />, path: '/incoming-shipments' },
          { text: 'Supplier Invoices', icon: <ReceiptIcon />, path: '/supplier-invoices' },
          { text: 'PO Acknowledgements', icon: <AssignmentIcon />, path: '/supplier-onboarding' },
          { text: 'Supplier Analytics', icon: <AssessmentIcon />, path: '/supplier-analytics' },
        ],
      },
      {
        key: 'procurement',
        label: 'Procurement',
        icon: <HandshakeIcon />,
        path: '/srm/rfq',
        permission: 'srm.view_supplier',
        submenus: [
          { text: 'Vendor Master', icon: <AssignmentIcon />, path: '/srm/vendor-master', permission: 'srm.view_supplier' },
          { text: 'Purchase Requisitions', icon: <AssignmentIcon />, path: '/srm/purchase-requisitions', permission: 'srm.view_requisition' },
          { text: 'Replenishment', icon: <Inventory2Icon />, path: '/srm/replenishment', permission: 'pp.view_replenishment' },
          { text: 'Purchase Orders', icon: <AssignmentIcon />, path: '/materials/purchase-orders', permission: 'srm.view_purchase_order' },
          { text: 'PO Line Items', icon: <ReceiptIcon />, path: '/materials/purchase-orders/line-items', permission: 'srm.view_purchase_order' },
          { text: 'PO History', icon: <AccountTreeIcon />, path: '/srm/po-history', permission: 'srm.view_purchase_order' },
          { text: 'Request for Quote', icon: <RequestQuoteIcon />, path: '/srm/rfq', permission: 'srm.view_rfq' },
          { text: 'New RFQ', icon: <AddIcon />, path: '/srm/rfq/new', permission: 'srm.add_rfq' },
          { text: 'Request for Information', icon: <InfoOutlinedIcon />, path: '/srm/rfi', permission: 'srm.view_rfi' },
          { text: 'New RFI', icon: <AddIcon />, path: '/srm/rfi/new', permission: 'srm.add_rfi' },
          { text: 'Supplier Evaluations', icon: <AssessmentIcon />, path: '/srm/evaluations', permission: 'srm.view_evaluation' },
          { text: 'Evaluation Templates', icon: <AssignmentIcon />, path: '/srm/evaluation-templates', permission: 'srm.view_evaluation' },
          { text: 'Vendor Expeditor', icon: <LocalShippingIcon />, path: '/srm/expeditor', permission: 'srm.view_expeditor' },
          // FS-P2P-01: vendor controls (statutory status, bank-change log, schedule adherence), import LCs, confirmations
          { text: 'P2P Performance', icon: <InsightsIcon />, path: '/srm/p2p-kpis', permission: 'srm.view_p2p_kpis' },
          { text: 'Vendor Controls', icon: <VerifiedUserIcon />, path: '/srm/p2p-controls', permissionAny: ['srm.view_supplier', 'srm.view_bank_change_log', 'srm.view_purchase_order'] },
          { text: 'Import LCs', icon: <AccountBalanceIcon />, path: '/srm/import-lc', permissionAny: ['fi_ap.view_import_lc', 'fi_ap.manage_import_lc'] },
          { text: 'Balance Confirmations', icon: <FactCheckIcon />, path: '/srm/confirmations', permissionAny: ['fi_ap.view_confirmations', 'fi_ap.run_confirmations', 'fi_ap.record_confirmation_reply'] },
          { text: 'Vendor Capacity', icon: <AssessmentIcon />, path: '/srm/vendor-capacity', permission: 'srm.view_capacity' },
          { text: 'Sub-Contracting', icon: <LocalShippingIcon />, path: '/srm/subcontract', permission: 'mm.view_subcontract_po' },
          // FS-P2P-02: job-work lots against the BOM norm, recovery, rate cards, job workers
          { text: 'Job Work', icon: <FactCheckIcon />, path: '/srm/job-work', permission: 'mm.view_subcontract_po' },
          // FS-P2P-03: chain movement, direct delivery, challan e-Way Bills, ITC-04, challan ageing
          { text: 'Job-work Movements', icon: <LocalShippingIcon />, path: '/srm/job-work-movements', permission: 'mm.view_subcontract_po' },
          { text: 'Service Entry Sheets', icon: <AssignmentIcon />, path: '/srm/service-entry-sheets', permission: 'ses.view_ses' },
          { text: 'Purchase Returns', icon: <LocalShippingIcon />, path: '/srm/purchase-returns', permission: 'pret.view_return' },
        ],
      },
      {
        key: 'contract-management',
        label: 'Contract Mgt',
        icon: <DescriptionIcon />,
        path: '/contract-management/clauses',
        permission: 'ctm.view_contract',
        submenus: [
          { text: 'Clause Library', icon: <GavelIcon />, path: '/contract-management/clauses' },
          { text: 'Templates', icon: <ViewModuleIcon />, path: '/contract-management/templates' },
          { text: 'Vendor Contracts', icon: <HandshakeIcon />, path: '/contract-management/contracts' },
          { text: 'Digital Signatures', icon: <DrawIcon />, path: '/contract-management/digital-signatures' },
        ],
      },
      {
        key: 'rim',
        label: 'Relationship Intelligence',
        icon: <HubIcon />,
        path: '/rim',
        permission: 'rim.view_graph',
        submenus: [
          { text: 'Graph Explorer', icon: <HubIcon />, path: '/rim' },
          { text: 'People', icon: <PeopleIcon />, path: '/rim/people' },
          { text: 'Life Events', icon: <TimelineIcon />, path: '/rim/life-events' },
          { text: 'Media & Documents', icon: <DescriptionIcon />, path: '/rim/media' },
        ],
      },
      {
        key: 'production',
        label: 'Production',
        icon: <PrecisionManufacturingIcon />,
        path: '/production',
        permission: 'pp.view_production',
        submenus: [
          { text: 'Plants', icon: <FactoryIcon />, path: '/production/plants' },
          { text: 'Work Centers', icon: <EngineeringIcon />, path: '/production/work-centers' },
          { text: 'Machines', icon: <PrecisionManufacturingIcon />, path: '/production/machines' },
          { text: 'Operation Standard Texts', icon: <DescriptionIcon />, path: '/production/operation-standard-texts' },
          { text: 'Shift Calendars', icon: <ScheduleIcon />, path: '/production/shift-calendars' },
          { text: 'Holiday Types', icon: <EventNoteIcon />, path: '/production/holiday-types' },
          { text: 'Holiday Calendars', icon: <EventBusyIcon />, path: '/production/holiday-calendars' },
          { text: 'Holidays', icon: <EventAvailableIcon />, path: '/production/holidays' },
          { text: 'Capacity Profiles', icon: <SpeedIcon />, path: '/production/capacity-profiles' },
          { text: 'Bill of Materials', icon: <TimelineIcon />, path: '/production/bom', permission: 'pp.view_bom' },
          { text: 'Change Requests', icon: <ChangeCircleIcon />, path: '/production/change-requests', permission: 'pp.view_change_order' },
          { text: 'Change Orders', icon: <ChangeCircleIcon />, path: '/production/change-orders', permission: 'pp.view_change_order' },
          { text: 'BOM Studio', icon: <AccountTreeIcon />, path: '/production/bom-studio' },
          { text: 'BOM Beta', icon: <AccountTreeIcon />, path: '/production/bom-beta' },
          { text: 'Pre-Packs', icon: <Inventory2Icon />, path: '/production/pre-packs', permission: 'pp.view_prepack' },
          { text: 'Sales Order BOM', icon: <ShoppingCartIcon />, path: '/production/so-bom' },
          { text: 'Routing', icon: <AltRouteIcon />, path: '/production/routing' },
          { text: 'Component Allocation', icon: <AccountTreeIcon />, path: '/production/component-allocation' },
          { text: 'Production Orders', icon: <AssignmentIcon />, path: '/production/orders' },
          { text: 'Scheduling', icon: <CalendarMonthIcon />, path: '/production/scheduling' },
          { text: 'Gantt Chart', icon: <TimelineIcon />, path: '/production/scheduling/gantt' },
          { text: 'Capacity Planning', icon: <PrecisionManufacturingIcon />, path: '/production/capacity-planning' },
          { text: 'CRP Report', icon: <AssessmentIcon />, path: '/production/capacity/crp-report' },
          { text: 'Planning Board', icon: <CalendarMonthIcon />, path: '/production/scheduling/board', permission: 'pp.view_schedule' },
          { text: 'Scheduling Capability', icon: <TuneIcon />, path: '/production/capability', permission: 'pp.view_capability' },
          { text: 'Shop Floor', icon: <FactoryIcon />, path: '/production/shop-floor' },
          { text: 'Production Confirmation', icon: <AssignmentIcon />, path: '/production/confirmation' },
          { text: 'MRP Runs', icon: <ViewModuleIcon />, path: '/production/mrp/runs' },
          { text: 'Planned Orders', icon: <TimelineIcon />, path: '/production/mrp/planned-orders' },
          { text: 'SO Reservations', icon: <TimelineIcon />, path: '/production/mrp/so-reservations', permission: 'production_planning.view_so_reservation' },
          { text: 'Missing Parts', icon: <WarningAmberIcon />, path: '/production/reports/missing-parts' },
          { text: 'WIP Subledger', icon: <AccountBalanceIcon />, path: '/production/cost/wip-ledger', permission: 'production_planning.view_production_cost' },
          { text: 'PI Sheets', icon: <AssignmentIcon />, path: '/pi-sheet', permission: 'pi.view_sheet' },
          { text: 'PI Recipe Templates', icon: <AltRouteIcon />, path: '/pi-sheet/recipes', permission: 'pi.view_recipe' },
          { text: 'Shop-Floor Data', icon: <FactoryIcon />, path: '/pi-sheet/shopfloor', permission: 'shopfloor.view_data' },
          { text: 'Shop-Floor Analytics', icon: <TimelineIcon />, path: '/pi-sheet/analytics', permission: 'shopfloor.view_data' },
        ],
      },
      {
        key: 'quality-management',
        label: 'Quality Management',
        icon: <VerifiedUserIcon />,
        path: '/qm/inspection-lots',
        // permissionAny: an Inline QC (FS-QM-01) holds qm.record_inline_checks only.
        permissionAny: ['qm.view_inspection_lot', 'qm.record_inline_checks'],
        submenus: [
          { text: 'Inspection Work List', icon: <FactCheckIcon />, path: '/qm/capture', permissionAny: ['qm.view_inspection_lot', 'qm.record_inline_checks'] },
          { text: 'Inspection Lots', icon: <FactCheckIcon />, path: '/qm/inspection-lots', permission: 'qm.view_inspection_lot' },
          { text: 'Defect Records', icon: <AssignmentIcon />, path: '/qm/defect-records', permission: 'qm.view_inspection_lot' },
          { text: 'Inspection Samples', icon: <AssignmentIcon />, path: '/qm/inspection-samples', permission: 'qm.view_inspection_lot' },
          { text: 'Usage Decisions', icon: <GavelIcon />, path: '/qm/usage-decisions', permission: 'qm.view_inspection_lot' },
          { text: 'Inspection Plans', icon: <AssignmentIcon />, path: '/qm/inspection-plans', permission: 'qm.view_master_data' },
          { text: 'Inspection Plan Operations', icon: <AssignmentIcon />, path: '/qm/inspection-plan-operations', permission: 'qm.view_master_data' },
          { text: 'Measurement Charts', icon: <StraightenIcon />, path: '/qm/measurement-charts', permission: 'qm.view_master_data' },
          { text: 'Non-Conformances', icon: <AssignmentIcon />, path: '/qm/ncr', permission: 'qm.view_ncr' },
          { text: 'CAPA', icon: <FactCheckIcon />, path: '/qm/capa', permission: 'qm.view_capa' },
          { text: 'Customer Complaints', icon: <AssignmentIcon />, path: '/qm/complaints/customer', permission: 'qm.view_complaint' },
          { text: 'Vendor Complaints', icon: <AssignmentIcon />, path: '/qm/complaints/vendor', permission: 'qm.view_complaint' },
          { text: 'Quality Audits', icon: <FactCheckIcon />, path: '/qm/audits', permission: 'qm.view_audit' },
          { text: 'Audit Dashboard', icon: <BarChartIcon />, path: '/qm/audit-dashboard', permission: 'qm.view_audit' },
          { text: 'Audit Programme', icon: <AssignmentIcon />, path: '/qm/audit-programme', permission: 'qm.view_audit' },
          { text: 'Audit Checklist Templates', icon: <AssignmentIcon />, path: '/qm/audit-templates', permission: 'qm.view_audit' },
          { text: 'Audit Verification', icon: <FactCheckIcon />, path: '/qm/audit-verification', permission: 'qm.verify_audit_finding' },
          { text: 'My Audit Actions', icon: <FactCheckIcon />, path: '/qm/my-audit-actions', permissionAny: ['qm.respond_audit_finding', 'qm.view_audit'] },
          { text: 'Line Checks', icon: <FactCheckIcon />, path: '/qm/line-checks', permission: 'qm.record_line_checks' },
          { text: 'Audit Set-up', icon: <AssignmentIcon />, path: '/qm/audit-setup', permission: 'qm.view_audit' },
          { text: 'Lot Release', icon: <FactCheckIcon />, path: '/qm/releases', permissionAny: ['qm.view_release', 'qm.release_lot', 'qm.manage_clearance'] },
          { text: 'Traceability Cockpit', icon: <AssignmentIcon />, path: '/qm/traceability', permissionAny: ['qm.view_traceability', 'qm.manage_containment'] },
          { text: 'Containment', icon: <AssignmentIcon />, path: '/qm/containments', permissionAny: ['qm.view_traceability', 'qm.manage_containment'] },
          { text: 'Shelf Life', icon: <StorageIcon />, path: '/qm/shelf-life', permissionAny: ['qm.view_release', 'qm.manage_shelf_life'] },
          { text: 'Retained Samples', icon: <StorageIcon />, path: '/qm/retained-samples', permissionAny: ['qm.view_release', 'qm.manage_retained_samples'] },
          { text: 'Release Reports', icon: <BarChartIcon />, path: '/qm/release-reports', permissionAny: ['qm.view_release', 'qm.view_traceability', 'mm.override_fifo'] },
          { text: 'Release Set-up', icon: <AssignmentIcon />, path: '/qm/release-setup', permissionAny: ['qm.view_release', 'qm.manage_release_setup', 'qm.manage_shelf_life'] },
          { text: 'Calibration', icon: <StorageIcon />, path: '/qm/calibration', permission: 'qm.view_calibration' },
          // FS-QM-06: calibration impact, MSA studies, daily checks, reports
          { text: 'Calibration Impact', icon: <FactCheckIcon />, path: '/qm/calibration-impact', permissionAny: ['qm.view_calibration', 'qm.assess_calibration_impact'] },
          { text: 'MSA Studies', icon: <BarChartIcon />, path: '/qm/msa-studies', permissionAny: ['qm.view_calibration', 'qm.manage_msa', 'qm.decide_msa'] },
          { text: 'Daily Checks', icon: <FactCheckIcon />, path: '/qm/daily-checks', permissionAny: ['qm.view_calibration', 'qm.record_daily_checks'] },
          { text: 'Calibration Reports', icon: <BarChartIcon />, path: '/qm/calibration-reports', permissionAny: ['qm.view_calibration'] },
          // FS-QM-07: certification register and quality document control
          { text: 'Certifications', icon: <AssignmentIcon />, path: '/qm/certifications', permissionAny: ['qm.view_certifications', 'qm.manage_certifications'] },
          { text: 'Quality Documents', icon: <AssignmentIcon />, path: '/qm/documents', permissionAny: ['qm.view_quality_documents', 'qm.manage_quality_documents', 'qm.issue_controlled_copies'] },
          { text: 'Controlled Copies', icon: <FactCheckIcon />, path: '/qm/controlled-copies', permissionAny: ['qm.view_quality_documents', 'qm.issue_controlled_copies'] },
          { text: 'Document Types', icon: <AssignmentIcon />, path: '/qm/document-types', permissionAny: ['qm.view_quality_documents', 'qm.manage_document_types'] },
          { text: 'Record Retention', icon: <StorageIcon />, path: '/qm/record-retention', permissionAny: ['qm.view_quality_documents', 'qm.manage_record_retention'] },
          { text: 'Inspection Reports', icon: <BarChartIcon />, path: '/qm/inspection-reports', permission: 'qm.view_inspection_lot' },
          { text: 'Switching Keys', icon: <AssignmentIcon />, path: '/qm/switching-keys', permission: 'qm.view_inspection_lot' },
          { text: 'Sampling Schemes', icon: <AssignmentIcon />, path: '/qm/sampling-schemes', permission: 'qm.view_master_data' },
          { text: 'Sampling Tables (Z1.4)', icon: <AssignmentIcon />, path: '/qm/sampling-tables', permission: 'qm.view_master_data' },
          { text: 'Incoming Work List', icon: <FactCheckIcon />, path: '/qm/incoming-worklist', permission: 'qm.view_inspection_lot' },
          { text: 'Incoming Inspection Set-up', icon: <AssignmentIcon />, path: '/qm/incoming-setup', permission: 'qm.view_master_data' },
          { text: 'Supplier Certificates', icon: <AssignmentIcon />, path: '/qm/supplier-certificates', permission: 'qm.view_supplier_quality' },
          { text: 'Supplier Scorecards', icon: <BarChartIcon />, path: '/qm/supplier-scorecards', permission: 'qm.view_supplier_quality' },
          { text: 'SCAR (8D)', icon: <FactCheckIcon />, path: '/qm/scars', permission: 'qm.view_supplier_quality' },
          { text: 'Supplier Quality Blocks', icon: <GavelIcon />, path: '/qm/supplier-blocks', permission: 'qm.view_supplier_quality' },
          { text: 'Concessions', icon: <GavelIcon />, path: '/qm/concessions', permissionAny: ['qm.manage_concession', 'qm.approve_concession', 'qm.view_ncr'] },
          { text: 'Action Tracker', icon: <FactCheckIcon />, path: '/qm/action-tracker', permission: 'qm.view_action_tracker' },
          { text: 'Nonconformance Set-up', icon: <AssignmentIcon />, path: '/qm/nc-setup', permission: 'qm.view_master_data' },
          { text: 'Nonconformance Reports', icon: <BarChartIcon />, path: '/qm/nc-reports', permission: 'qm.view_ncr' },
          // FS-QM-08: quality dashboard, KPI set-up (targets, packs), management review, cost of quality
          { text: 'Quality Dashboard', icon: <BarChartIcon />, path: '/qm/quality-dashboard', permissionAny: ['qm.view_quality_dashboard', 'qm.manage_kpi_targets'] },
          { text: 'KPI Set-up', icon: <AssignmentIcon />, path: '/qm/kpi-setup', permissionAny: ['qm.view_quality_dashboard', 'qm.manage_kpi_targets'] },
          { text: 'Management Review', icon: <FactCheckIcon />, path: '/qm/management-review', permissionAny: ['qm.view_quality_dashboard', 'qm.manage_mgmt_review_pack'] },
          { text: 'Cost of Quality', icon: <BarChartIcon />, path: '/qm/cost-of-quality', permissionAny: ['qm.view_coq', 'qm.manage_coq_mapping', 'qm.publish_coq'] },
          // FS-QM-09: quality planning (PFMEA / control plans / review flags), lab test requests, SPC
          { text: 'Quality Planning', icon: <AssignmentIcon />, path: '/qm/quality-planning', permissionAny: ['qm.view_quality_planning', 'qm.manage_pfmea', 'qm.approve_pfmea', 'qm.manage_control_plan', 'qm.clear_quality_review'] },
          { text: 'Lab Tests', icon: <FactCheckIcon />, path: '/qm/lab-tests', permissionAny: ['qm.view_lab_tests', 'qm.manage_test_packages', 'qm.manage_test_requests'] },
          { text: 'SPC', icon: <BarChartIcon />, path: '/qm/spc', permissionAny: ['qm.view_spc', 'qm.manage_spc', 'qm.manage_spc_baseline'] },
          { text: 'Analytics', icon: <BarChartIcon />, path: '/qm/analytics', permission: 'qm.view_inspection_lot' },
          { text: 'Quality Roles', icon: <AssignmentIcon />, path: '/qm/quality-roles', permissionAny: ['qm.view_quality_documents', 'qm.view_certifications', 'qm.manage_quality_roles'] },
          // FS-QM-07 11: every user reads published quality documents and acknowledges them.
          { text: 'Published Documents', icon: <FactCheckIcon />, path: '/qm/published-documents' },
        ],
      },
      {
        key: 'batch-traceability',
        label: 'Batch Traceability',
        icon: <AccountTreeIcon />,
        path: '/trace',
        permission: 'trace.view_batch',
        submenus: [
          { text: 'Search & Overview', icon: <AccountTreeIcon />, path: '/trace', permission: 'trace.view_batch' },
          { text: 'Genealogy Tree', icon: <AccountTreeIcon />, path: '/trace/genealogy', permission: 'trace.view_genealogy' },
          { text: 'Stock Visibility', icon: <Inventory2Icon />, path: '/trace/stock', permission: 'trace.view_batch' },
          { text: 'Transaction History', icon: <ReceiptLongIcon />, path: '/trace/history', permission: 'trace.view_batch' },
          { text: 'Lot Recalls', icon: <CampaignIcon />, path: '/trace/recalls', permission: 'trace.view_recall' },
          { text: 'Quality Trace', icon: <FactCheckIcon />, path: '/trace/quality', permission: 'trace.view_batch' },
          { text: 'Expiry & Shelf-Life', icon: <EventBusyIcon />, path: '/trace/expiry', permission: 'trace.view_batch' },
          { text: 'Documents', icon: <DescriptionIcon />, path: '/trace/documents', permission: 'trace.view_batch' },
          { text: 'Risk Analysis', icon: <WarningAmberIcon />, path: '/trace/risk', permission: 'trace.view_dashboard' },
          { text: 'Dashboards', icon: <BarChartIcon />, path: '/trace/dashboard', permission: 'trace.view_dashboard' },
        ],
      },
      {
        key: 'ehs',
        label: 'Env. Health & Safety',
        icon: <HealthAndSafetyIcon />,
        path: '/ehs',
        permission: 'ehs.view_incident',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/ehs', permission: 'ehs.view_incident' },
          { text: 'Incidents', icon: <ReportProblemIcon />, path: '/ehs/incidents', permission: 'ehs.view_incident' },
          { text: 'Incident Types', icon: <CategoryIcon />, path: '/ehs/incident-types', permission: 'ehs.view_master_data' },
          { text: 'Severity Levels', icon: <WarningAmberIcon />, path: '/ehs/severity-levels', permission: 'ehs.view_master_data' },
          { text: 'Statuses', icon: <FactCheckIcon />, path: '/ehs/incident-statuses', permission: 'ehs.view_master_data' },
        ],
      },
      {
        key: 'customer-service',
        label: 'Customer Service',
        icon: <SupportAgentIcon />,
        path: '/customer-service',
        permission: 'cs.view_ticket',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/customer-service', permission: 'cs.view_ticket' },
          { text: 'Service Tickets', icon: <ReportProblemIcon />, path: '/customer-service/tickets', permission: 'cs.view_ticket' },
          { text: 'Customer Assets', icon: <DescriptionIcon />, path: '/customer-service/assets', permission: 'cs.view_asset' },
          { text: 'Repair Jobs', icon: <BuildIcon />, path: '/customer-service/repair-jobs', permission: 'cs.view_repair' },
        ],
      },
      {
        key: 'risk-management',
        label: 'Risk Management',
        icon: <GavelIcon />,
        path: '/rm/dashboard',
        permission: 'rm.view_risk',
        submenus: [
          { text: 'Risk Cockpit', icon: <DashboardIcon />, path: '/rm/dashboard', permission: 'rm.view_risk' },
          { text: 'Risk Register', icon: <AssessmentIcon />, path: '/rm/risk-register', permission: 'rm.view_risk' },
        ],
      },
      {
        key: 'compliance-management',
        label: 'Compliance',
        icon: <PolicyIcon />,
        path: '/cm/dashboard',
        permission: 'cm.view_dashboard',
        submenus: [
          { text: 'Compliance Cockpit', icon: <DashboardIcon />, path: '/cm/dashboard', permission: 'cm.view_dashboard' },
          { text: 'Obligations', icon: <FactCheckIcon />, path: '/cm/obligations', permission: 'cm.view_obligation' },
          { text: 'Compliance Calendar', icon: <CalendarMonthIcon />, path: '/cm/calendar', permission: 'cm.view_obligation' },
          { text: 'Tasks & Verification', icon: <AssignmentTurnedInIcon />, path: '/cm/tasks', permission: 'cm.view_obligation' },
          { text: 'Policies', icon: <PolicyIcon />, path: '/cm/policies', permission: 'cm.view_policy' },
          { text: 'Findings & CAPA', icon: <ReportProblemIcon />, path: '/cm/findings', permission: 'cm.view_finding' },
        ],
      },
      {
        key: 'design-studio',
        label: 'Design Studio',
        icon: <CheckroomIcon />,
        path: '/design-studio',
        permission: 'design.view_project',
        submenus: [
          { text: 'Design Projects', icon: <CheckroomIcon />, path: '/design-studio', permission: 'design.view_project' },
        ],
      },
      {
        key: 'plm',
        label: 'Product Lifecycle (PLM)',
        icon: <CheckroomIcon />,
        path: '/plm/styles',
        permission: 'plm.view_style',
        submenus: [
          { text: 'Styles', icon: <CheckroomIcon />, path: '/plm/styles', permission: 'plm.view_style' },
          { text: 'Sample Cost & Courier', icon: <LocalShippingIcon />, path: '/plm/sample-reports', permission: 'plm.view_sample' },
        ],
      },
      {
        key: 'cutting-room',
        label: 'Cutting Room',
        icon: <ContentCutIcon />,
        path: '/cutting/dashboard',
        permission: 'cr.view_marker',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/cutting/dashboard' },
          { text: 'Markers', icon: <StraightenIcon />, path: '/cutting/markers' },
          { text: 'Lay Slips', icon: <AssignmentIcon />, path: '/cutting/lay-slips' },
          { text: 'Cutting Jobs', icon: <ContentCutIcon />, path: '/cutting/jobs' },
          // Between Cutting Jobs and Cutting Notes because that is where the
          // operation sits: after cutting, before bundling.
          { text: 'Down-Size', icon: <SwapVertIcon />, path: '/cutting/down-size', permission: 'cr.view_down_size' },
          { text: 'Cutting Notes', icon: <AssignmentIcon />, path: '/cutting/cutting-notes' },
          { text: 'Bundle Labels', icon: <AssignmentIcon />, path: '/cutting/bundle-labels' },
          { text: 'QC Inspection', icon: <FactCheckIcon />, path: '/cutting/qc' },
          { text: 'Master Bags', icon: <InventoryIcon />, path: '/cutting/master-bags' },
          { text: 'Reports', icon: <BarChartIcon />, path: '/cutting/reports' },
          // Master data, so it sits last and is gated on the permission
          // that actually owns the catalogue — maintaining a price is a
          // costing job, not a spreader's. Recording waste against these
          // types needs no such permission.
          { text: 'Waste Types & Rates', icon: <DeleteSweepIcon />, path: '/cutting/waste-types', permission: 'mm.view_rejection' },
        ],
      },
      {
        key: 'piece-tracking',
        label: 'Piece Tracking',
        icon: <SpeedIcon />,
        path: '/production/line-board',
        // The BOARD permission gates the menu, not the analytics one: the
        // board is the line's own live state and should be broadly held —
        // a floor display needs it. The analytics carry operator and defect
        // attribution, which is different information about named people.
        permission: 'pt.view_board',
        submenus: [
          { text: 'Line Board', icon: <SpeedIcon />, path: '/production/line-board', permission: 'pt.view_board' },
          { text: 'Scan', icon: <QrCodeScannerIcon />, path: '/production/piece-tracking/scan', permission: 'pt.record_scan' },
          { text: 'Pieces', icon: <InventoryIcon />, path: '/production/piece-tracking/pieces', permission: 'pt.view_piece' },
          { text: 'Quality Analytics', icon: <BarChartIcon />, path: '/production/piece-tracking/analytics', permission: 'pt.view_analytics' },
          { text: 'Setup', icon: <SettingsIcon />, path: '/production/piece-tracking/setup', permission: 'pt.manage_profile' },
        ],
      },
      // Shop-floor breakdown flow (mobile). Its own top-level entry, because a
      // production supervisor holds pm.report_breakdown and nothing else in PM —
      // under the Plant Maintenance section they would see that whole menu.
      {
        key: 'machine-breakdown',
        label: 'Machine Breakdown',
        icon: <BuildCircleIcon />,
        path: '/pm/breakdown-mobile',
        permissionAny: ['pm.report_breakdown', 'pm.assign_breakdown', 'pm.work_breakdown', 'pm.issue_spares'],
      },
      {
        key: 'plant-maintenance',
        label: 'Plant Maintenance',
        icon: <EngineeringIcon />,
        path: '/pm',
        permission: 'pm.view_maintenanceplan',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/pm' },
          { text: 'Maintenance Plans', icon: <AssignmentIcon />, path: '/pm/maintenance-plans' },
          { text: 'Schedules', icon: <CalendarMonthIcon />, path: '/pm/maintenance-schedules' },
          { text: 'Breakdown Reports', icon: <InventoryIcon />, path: '/pm/breakdown-reports' },
          { text: 'Machine QR Labels', icon: <QrCode2Icon />, path: '/pm/machine-qr-labels', permissionAny: ['pm.assign_breakdown', 'pm.change_breakdownreport'] },
          { text: 'Work Orders', icon: <EngineeringIcon />, path: '/pm/work-orders' },
          { text: 'External Service', icon: <BuildIcon />, path: '/pm/external-service', permission: 'pm.view_externalserviceorder' },
          { text: 'Returnable Assets Register', icon: <ReceiptLongIcon />, path: '/pm/returnable-register', permission: 'pm.view_returnable_register' },
          { text: 'Equipment Health', icon: <TimelineIcon />, path: '/pm/equipment-health' },
          { text: 'Equipment Specifications', icon: <SettingsIcon />, path: '/pm/equipment-specifications' },
          { text: 'Operating Parameters', icon: <SpeedIcon />, path: '/pm/equipment-parameters' },
          { text: 'Condition Readings', icon: <SpeedIcon />, path: '/pm/condition-readings' },
          { text: 'Maintenance Tasks', icon: <AssignmentIcon />, path: '/pm/maintenance-tasks' },
          { text: 'Task Executions', icon: <PlayCircleIcon />, path: '/pm/task-executions' },
          { text: 'Maintenance Resources', icon: <InventoryIcon />, path: '/pm/maintenance-resources' },
          { text: 'Work Order Labor', icon: <WorkIcon />, path: '/pm/work-order-labor' },
          { text: 'Work Order Parts', icon: <InventoryIcon />, path: '/pm/work-order-parts' },
          { text: 'Defect Analyses', icon: <BugReportIcon />, path: '/pm/defect-analyses' },
          { text: 'Failure History', icon: <HistoryIcon />, path: '/pm/failure-history' },
          { text: 'Predictive Models', icon: <PsychologyIcon />, path: '/pm/predictive-models' },
          { text: 'Failure Predictions', icon: <WarningAmberIcon />, path: '/pm/failure-predictions' },
          { text: 'Anomaly Detections', icon: <NotificationImportantIcon />, path: '/pm/anomaly-detections' },
        ],
      },
      {
        key: 'textile-costing',
        label: 'Textile Costing',
        icon: <CalculateIcon />,
        path: '/textile-costing/opportunities',
        permission: 'tc.view_costing',
        submenus: [
          { text: 'Workspace', icon: <GridViewIcon />, path: '/textile-costing/workspace' },
          { text: 'Executive Summary', icon: <BusinessIcon />, path: '/textile-costing/opportunities' },
          { text: 'Cut Plans', icon: <CalculateIcon />, path: '/textile-costing/cutplans' },
          { text: 'Cost Worksheet', icon: <GridViewIcon />, path: '/textile-costing/cut-plan-worksheet' },
          { text: 'Greige Costing', icon: <CalculateIcon />, path: '/textile-costing/greige' },
          { text: 'Finished Costing', icon: <CalculateIcon />, path: '/textile-costing/finished' },
          { text: 'Made-Up Costing', icon: <CalculateIcon />, path: '/textile-costing/madeup' },
          { text: 'Terry Fabric', icon: <CalculateIcon />, path: '/textile-costing/terry-fabric' },
          { text: 'Terry Made-Up', icon: <CalculateIcon />, path: '/textile-costing/terry-madeup' },
          { text: 'Set Costings', icon: <CalculateIcon />, path: '/textile-costing/set' },
          { text: 'Component Master', icon: <CategoryIcon />, path: '/textile-costing/component-master' },
          { text: 'Price Revisions', icon: <PriceChangeIcon />, path: '/textile-costing/price-revisions' },
          { text: 'Costing Report', icon: <AssignmentIcon />, path: '/costing/costing-report/', external: true },
        ],
      },
      {
        key: 'garment-costing',
        label: 'Garment Costing',
        icon: <CheckroomIcon />,
        path: '/garment-costing/workspace',
        permission: 'gc.view_costing',
        submenus: [
          { text: 'Workspace', icon: <GridViewIcon />, path: '/garment-costing/workspace' },
          { text: 'Workspace (Grid • beta)', icon: <GridViewIcon />, path: '/garment-costing/workspace-v2' },
          { text: 'All Styles', icon: <CalculateIcon />, path: '/garment-costing/styles' },
          { text: 'Dashboard', icon: <AssignmentIcon />, path: '/garment-costing/dashboard' },
          { text: 'Costing Results', icon: <AssessmentIcon />, path: '/garment-costing/results', permission: 'gc.view_costing' },
          { text: 'Commercial Masters', icon: <SettingsIcon />, path: '/garment-costing/masters', permission: 'gc.view_costing' },
        ],
      },
      {
        key: 'ecommerce',
        label: 'E-Commerce',
        icon: <StorefrontIcon />,
        path: '/shop',
        permission: 'ecom.view_store',
        submenus: [
          { text: 'Storefront', icon: <StorefrontIcon />, path: '/shop' },
          { text: 'Products', icon: <InventoryIcon />, path: '/shop/products' },
          { text: 'Cart', icon: <ShoppingCartIcon />, path: '/shop/cart' },
          { text: 'Orders', icon: <ReceiptIcon />, path: '/shop/my-orders' },
          { text: 'Wishlist', icon: <FavoriteIcon />, path: '/shop/wishlist' },
          { text: 'Compare', icon: <CompareIcon />, path: '/shop/comparison' },
        ],
      },
      {
        key: 'pos',
        label: 'Point of Sale',
        icon: <PointOfSaleIcon />,
        path: '/pos',
        permission: 'pos.use_pos',
        submenus: [
          { text: 'POS Cockpit', icon: <PointOfSaleIcon />, path: '/pos' },
          { text: 'Stores Dashboard', icon: <AssessmentIcon />, path: '/pos/stores-dashboard' },
          { text: 'POS Terminal', icon: <ShoppingCartIcon />, path: '/pos/terminal' },
          { text: 'Sales Points', icon: <StorefrontIcon />, path: '/pos/sales-points' },
          { text: 'Sales Point Pricing', icon: <LocalOfferIcon />, path: '/pos/sales-point-pricing', permission: 'pos.view_pricing' },
          { text: 'Cash Sales', icon: <ReceiptIcon />, path: '/pos/cash-sales' },
          { text: 'Cashier Shifts', icon: <CalendarMonthIcon />, path: '/pos/shifts' },
          { text: 'Daily Settlements', icon: <AccountBalanceIcon />, path: '/pos/settlements' },
          { text: 'Returns', icon: <AssignmentIcon />, path: '/pos/returns' },
        ],
      },
    ],
  },
  {
    id: 'visitor-management',
    header: 'FACILITIES',
    items: [
      {
        key: 'visitor-mgmt',
        label: 'Visitor Management',
        icon: <MeetingRoomIcon />,
        path: '/visitor-management',
        permissionAny: ['vms.view_visitor', 'vms.checkin_visitor'],
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/visitor-management', permission: 'vms.view_visitor' },
          { text: 'Check-In / Check-Out', icon: <BadgeIcon />, path: '/visitor-management/checkin', permission: 'vms.view_visitor' },
          { text: 'Gatekeeper', icon: <MeetingRoomIcon />, path: '/visitor-management/gatekeeper', permission: 'vms.checkin_visitor' },
          { text: 'Visit Requests', icon: <AssignmentIcon />, path: '/visitor-management/visit-requests', permission: 'vms.view_visitor' },
          { text: 'Visitors', icon: <PeopleIcon />, path: '/visitor-management/visitors', permission: 'vms.view_visitor' },
          { text: 'Visit Logs', icon: <TimelineIcon />, path: '/visitor-management/logs', permission: 'vms.view_visitor' },
          { text: 'Locations', icon: <LocationCityIcon />, path: '/visitor-management/locations', permission: 'vms.view_visitor' },
          { text: 'Blacklist', icon: <VerifiedUserIcon />, path: '/visitor-management/blacklist', permission: 'vms.view_visitor' },
        ],
      },
      {
        key: 'front-desk',
        label: 'Front Desk',
        icon: <MarkunreadMailboxIcon />,
        path: '/front-desk',
        permission: 'fd.view_entry',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/front-desk' },
          { text: 'My Inbox', icon: <MoveToInboxIcon />, path: '/front-desk/inbox' },
          { text: 'All Entries', icon: <AssignmentIcon />, path: '/front-desk/entries' },
          { text: 'New Entry', icon: <AddIcon />, path: '/front-desk/entries/new', permission: 'fd.add_entry' },
          { text: 'Goods Awaiting Receipt', icon: <Inventory2Icon />, path: '/front-desk/pending-goods-receipt', permission: 'fd.view_reports' },
          { text: 'Delegation Settings', icon: <PeopleAltIcon />, path: '/front-desk/delegation' },
        ],
      },
    ],
  },
  {
    id: 'exim',
    header: 'EXIM',
    items: [
      {
        key: 'exim-export-dashboard',
        label: 'EXIM',
        icon: <FlightTakeoffIcon />,
        path: '/exim',
        permission: 'exim.view_export',
        submenus: [
          { text: 'Export Dashboard', icon: <DashboardIcon />, path: '/exim/export-benefits-dashboard' },
          // Gated on crm.view_invoice, NOT exim.view_export: the page reads
          // crm.Invoice and that is what the API enforces. Gating it on an
          // exim codename would show the entry to people the endpoint refuses.
          { text: 'Customs Invoices', icon: <ReceiptIcon />, path: '/exim/customs-invoices', permission: 'crm.view_invoice' },
          { text: 'Import Consignments', icon: <Inventory2Icon />, path: '/exim/import-consignments', permission: 'exim.view_import' },
          { text: 'Authorisations', icon: <DescriptionIcon />, path: '/exim/authorisations', permission: 'exim.view_authorisation' },
          { text: 'Export Obligations', icon: <AssignmentIcon />, path: '/exim/export-obligations', permission: 'exim.view_eo_reports' },
          { text: 'Duty Setup', icon: <SettingsIcon />, path: '/exim/duty-setup', permission: 'exim.manage_duty_setup' },
        ],
      },
    ],
  },
  {
    id: 'forex',
    header: 'FOREX',
    items: [
      {
        key: 'forex-exchange-rates',
        label: 'Forex',
        icon: <CurrencyExchangeIcon />,
        path: '/forex',
        permission: 'forex.view_exchange_rate',
        submenus: [
          { text: 'Exchange Rates', icon: <SwapHorizIcon />, path: '/forex/exchange-rates' },
        ],
      },
    ],
  },
  {
    id: 'finance',
    header: 'FINANCE & ADMIN',
    items: [
      {
        key: 'financial',
        label: 'Accounting',
        icon: <AccountBalanceIcon />,
        path: '/financial',
        // Clicking the row opens the bento launcher as well as expanding the
        // group. `path` stays '/financial' — it is the navigation registry key
        // and the active-state anchor, and /financial/menu inherits its gate by
        // longest-prefix match.
        landingPath: '/financial/menu',
        permission: 'fi.view_gl_account',
        submenuGroups: [
          {
            header: 'General Ledger',
            items: [
              { text: 'Company Master', icon: <BusinessIcon />, path: '/financial/companies' },
              { text: 'Fiscal Years', icon: <CalendarMonthIcon />, path: '/financial/fiscal-years' },
              { text: 'Journal Entries', icon: <AssignmentIcon />, path: '/financial/journal-entries' },
              { text: 'Chart of Accounts', icon: <AccountBalanceIcon />, path: '/financial/chart-of-accounts' },
              { text: 'GL Accounts', icon: <AccountBalanceIcon />, path: '/financial/gl-accounts', permission: 'fi.view_gl_account' },
              { text: 'GL Account Ledger', icon: <AccountBalanceIcon />, path: '/gl-ledger' },
              { text: 'Financial Statements', icon: <TrendingUpIcon />, path: '/financial/statements', permission: 'fi.view_trial_balance' },
              { text: 'Profitability Analysis', icon: <TrendingUpIcon />, path: '/finance/profitability', permission: 'profitability.view_report' },
              { text: 'Profitability Mapping', icon: <TrendingUpIcon />, path: '/financial/profitability-mapping', permission: 'profitability.manage_mapping' },
              { text: 'Ratio Analysis', icon: <TrendingUpIcon />, path: '/management-accounting/ratio-analysis', permission: 'ma.view_ratio' },
              { text: 'Cash Management', icon: <AccountBalanceIcon />, path: '/cash-management', permission: 'cash.view_position' },
              { text: 'Asset Management', icon: <AccountBalanceIcon />, path: '/financial/asset-register' },
              { text: 'GL Open Items', icon: <ReceiptIcon />, path: '/financial/gl-open-items', permission: 'fi_gl.view_open_item' },
              { text: 'GL / Statutory Payments', icon: <AccountBalanceIcon />, path: '/financial/gl-payments', permission: 'fi_gl.view_gl_payment' },
              { text: 'AR/AP Set-off Runs', icon: <ReceiptIcon />, path: '/fi-netting/setoff-runs', permission: 'netting.view_setoff' },
              { text: 'Net Exposure', icon: <AccountBalanceIcon />, path: '/fi-netting/net-exposure', permission: 'netting.view_reports' },
              { text: 'Withholding Matrix (194Q/206C)', icon: <ReceiptIcon />, path: '/fi-netting/withholding-matrix', permission: 'netting.view_reports' },
              { text: 'Balance Transfer (Vendor / Customer / GL)', icon: <ReceiptIcon />, path: '/fi-netting/balance-transfer', permission: 'netting.view_transfer' },
              { text: 'Period Close', icon: <LockClockIcon />, path: '/financial/period-close', permission: 'fi.view_period_close' },
              { text: 'FX Revaluation', icon: <CurrencyExchangeIcon />, path: '/financial/fc-revaluation', permission: 'fi.view_fc_revaluation' },
            ],
          },
          {
            header: 'Bank',
            items: [
              { text: 'Bank Book', icon: <AccountBalanceIcon />, path: '/financial/bank-book', permission: 'bank.view_bank_book' },
              { text: 'Bank Reconciliation', icon: <AccountBalanceIcon />, path: '/bank-reconciliation' },
              { text: 'Bank Transactions', icon: <ReceiptIcon />, path: '/financial/bank-transactions', permission: 'bank.view_transaction' },
              { text: 'Payment Instruments', icon: <ReceiptIcon />, path: '/financial/payment-instruments', permission: 'bank.view_instrument' },
              { text: 'House Bank Accounts', icon: <AccountBalanceIcon />, path: '/fi-ap/house-banks', permission: 'fi_ap.manage_house_bank' },
              { text: 'AP Posting Configuration', icon: <SettingsIcon />, path: '/fi-ap/posting-config', permission: 'fi_ap.manage_posting_config' },
            ],
          },
          {
            header: 'Statutory Reports',
            permission: 'fi.view_schedule_iii',
            subGroups: [
              {
                header: 'Schedule III',
                permission: 'fi.view_schedule_iii',
                items: [
                  { text: 'Statements Pack', icon: <DescriptionIcon />, path: '/financial/schedule-iii', permission: 'fi.view_schedule_iii' },
                  { text: 'P&L', icon: <DescriptionIcon />, path: '/financial/schedule-iii-pl', permission: 'fi.view_schedule_iii' },
                  { text: 'Balance Sheet', icon: <AccountBalanceIcon />, path: '/financial/schedule-iii-bs', permission: 'fi.view_schedule_iii' },
                  { text: 'Cash Flows', icon: <TrendingUpIcon />, path: '/financial/schedule-iii-cf', permission: 'fi.view_schedule_iii' },
                  { text: 'Changes in Equity', icon: <AccountBalanceIcon />, path: '/financial/schedule-iii-soce', permission: 'fi.view_schedule_iii' },
                  { text: 'Notes & Policies', icon: <DescriptionIcon />, path: '/financial/fs-notes', permission: 'fi.view_schedule_iii' },
                  { text: 'Tagging Workbench', icon: <AssignmentIcon />, path: '/financial/statement-tagging', permission: 'fi.manage_statement_tags' },
                ],
              },
              {
                header: 'Statutory Suite',
                items: [
                  { text: 'Fixed Asset Schedule', icon: <AccountBalanceIcon />, path: '/fi-aa/asset-schedule', permission: 'fi_aa.view_aa_reports' },
                  { text: 'TDS / TCS', icon: <ReceiptIcon />, path: '/financial/tds-tcs', permission: 'fi_tds.view_reports' },
                  { text: 'Related Parties', icon: <AccountBalanceIcon />, path: '/financial/related-parties', permission: 'fi.view_related_party' },
                  { text: 'Consolidated FS', icon: <AccountBalanceIcon />, path: '/financial/consolidation', permission: 'fi.view_consolidated_fs' },
                  { text: 'XBRL Filing', icon: <DescriptionIcon />, path: '/financial/xbrl', permission: 'fi.view_schedule_iii' },
                  { text: 'Audit Trail (Rule 11g)', icon: <VerifiedUserIcon />, path: '/financial/audit-trail', permission: 'fi.view_audit_trail' },
                ],
              },
            ],
          },
          {
            header: 'Receivables',
            items: [
              { text: 'AR Dashboard', icon: <AccountBalanceIcon />, path: '/receivables-dashboard', permission: 'fi_ar.view_receivables' },
              { text: 'Customer Ledger', icon: <AccountBalanceIcon />, path: '/customer-ledger', permission: 'fi_ar.view_receivables' },
              { text: 'Direct Invoices', icon: <ReceiptIcon />, path: '/fi-ar/direct-invoices', permission: 'fi_ar.view_direct_invoice' },
              { text: 'Unpaid Invoices', icon: <ReceiptIcon />, path: '/unpaid-invoices', permission: 'fi_ar.view_receivables' },
              { text: 'Customer Reconciliation', icon: <AssignmentIcon />, path: '/customer-reconciliation', permission: 'fi_ar.view_customer_recon' },
              { text: 'Dunning Letters', icon: <MarkunreadMailboxIcon />, path: '/dunning-letters', permission: 'fi_ar.view_dunning' },
              { text: 'Payments Received', icon: <PaymentIcon />, path: '/financial/payments-received', permission: 'fi_ar.view_customer_payment' },
              { text: 'GST Advance Accounts', icon: <SettingsIcon />, path: '/financial/gst-advance-config', permission: 'fi_ar.view_gst_advance' },
              { text: 'Payment Allocation', icon: <LinkIcon />, path: '/payment-allocation', permission: 'fi_ar.view_receivables' },
              { text: 'AR Debit/Credit Note', icon: <ReceiptIcon />, path: '/fi-notes?type=AR', permission: 'fi_notes.view_note' },
              { text: 'Credit Notes', icon: <ReceiptIcon />, path: '/fi-notes?type=AR_CN', permission: 'fi_notes.view_note' },
            ],
          },
          {
            header: 'Payables',
            permission: 'fi_ap.view_payment',
            items: [
              { text: 'AP Dashboard', icon: <AccountBalanceIcon />, path: '/payables-dashboard' },
              { text: 'Vendor Invoices', icon: <ReceiptIcon />, path: '/fi-ap/vendor-invoices' },
              { text: 'Due Invoices', icon: <PaymentIcon />, path: '/fi-ap/due-invoices', permission: 'fi_ap.view_payment' },
              { text: 'Payment Runs', icon: <PlayArrowIcon />, path: '/fi-ap/payment-runs', permission: 'fi_ap.view_payment' },
              { text: 'Payment Register', icon: <PaymentIcon />, path: '/fi-ap/payments', permission: 'fi_ap.view_payment' },
              { text: 'Manual Payment', icon: <AddIcon />, path: '/fi-ap/manual-payment', permission: 'fi_ap.create_payment' },
              { text: 'Advance Payment', icon: <AccountBalanceIcon />, path: '/fi-ap/advance-payment', permission: 'fi_ap.create_payment' },
              { text: 'Down Payments', icon: <AccountBalanceIcon />, path: '/fi-ap/down-payments', permission: 'fi_ap_dp.view_downpayment' },
              { text: 'Vendor Allocation', icon: <LinkIcon />, path: '/fi-ap/allocation', permission: 'fi_ap.view_payment' },
              { text: 'GR/IR Report', icon: <AssignmentIcon />, path: '/fi-ap/reports/gr-ir', permission: 'fi_ap.view_ap_reports' },
              { text: 'Outstanding Payables', icon: <AssignmentIcon />, path: '/fi-ap/reports/outstanding', permission: 'fi_ap.view_ap_reports' },
              { text: 'MSME Outstanding', icon: <VerifiedUserIcon />, path: '/fi-ap/reports/msme-outstanding', permission: 'fi_ap.view_ap_reports' },
              { text: 'Import LCs', icon: <AccountBalanceIcon />, path: '/srm/import-lc', permissionAny: ['fi_ap.view_import_lc', 'fi_ap.manage_import_lc'] },
              { text: 'Balance Confirmations', icon: <FactCheckIcon />, path: '/srm/confirmations', permissionAny: ['fi_ap.view_confirmations', 'fi_ap.run_confirmations'] },
              { text: 'Cash Forecast', icon: <TrendingUpIcon />, path: '/fi-ap/reports/cash-forecast', permission: 'fi_ap.view_ap_reports' },
              { text: 'TDS Report', icon: <DescriptionIcon />, path: '/fi-ap/reports/tds', permission: 'fi_ap.view_ap_reports' },
              { text: 'Vendor Ledger', icon: <AccountBalanceIcon />, path: '/vendor-ledger' },
              { text: 'Vendor Open Items', icon: <AssignmentIcon />, path: '/fi-ap/open-items', permission: 'fi_ap.view_payment' },
              { text: 'Vendor Reconciliation', icon: <AssignmentIcon />, path: '/vendor-reconciliation' },
              { text: 'AP Debit/Credit Note', icon: <ReceiptIcon />, path: '/fi-notes?type=AP', permission: 'fi_notes.view_note' },
              { text: 'Credit Notes', icon: <ReceiptIcon />, path: '/fi-notes?type=AP_CN', permission: 'fi_notes.view_note' },
            ],
          },
          {
            header: 'Reports',
            items: [
              { text: 'Vendor Balance List', icon: <AssignmentIcon />, path: '/finance/reports/vendor-balances', permission: 'fi_ap.view_ap_reports' },
              { text: 'Customer Balance List', icon: <AssignmentIcon />, path: '/finance/reports/customer-balances', permission: 'fi_ar.view_ar_reports' },
            ],
          },
          {
            header: 'Supplier Invoice Mgmt',
            permission: 'sim.view_invoice',
            items: [
              { text: 'Dashboard', icon: <DashboardIcon />, path: '/supplier-invoice-management' },
              { text: 'Process Invoice', icon: <CloudUploadIcon />, path: '/supplier-invoice-management/process', permission: 'sim.add_invoice' },
              { text: 'All Invoices', icon: <AssignmentIcon />, path: '/supplier-invoice-management/invoices' },
              { text: 'Approval Inbox', icon: <ApprovalIcon />, path: '/supplier-invoice-management/approval-inbox' },
              { text: 'Exceptions', icon: <VerifiedUserIcon />, path: '/supplier-invoice-management/exceptions' },
              { text: 'Approval Rules', icon: <SettingsIcon />, path: '/supplier-invoice-management/approval-rules', permission: 'sim.manage_rules' },
            ],
          },
          {
            header: 'Cash Journal',
            permission: 'cj.view_cash_journal',
            items: [
              { text: 'Dashboard', icon: <DashboardIcon />, path: '/finance/cash-journal/dashboard', permission: 'cj.view_cash_journal' },
              { text: 'Cash Journals', icon: <AccountBalanceIcon />, path: '/finance/cash-journal', permission: 'cj.view_cash_journal' },
              { text: 'Transactions', icon: <ReceiptIcon />, path: '/finance/cash-journal/transactions', permission: 'cj.view_transaction' },
              { text: 'Day Close', icon: <CalculateIcon />, path: '/finance/cash-journal/day-close', permission: 'cj.manage_journal' },
            ],
          },
          {
            header: 'Controlling',
            items: [
              { text: 'Internal Orders', icon: <AssignmentIcon />, path: '/finance/internal-orders', permission: 'io.change_order' },
              { text: 'Post Cost', icon: <AssignmentIcon />, path: '/finance/internal-orders/post-cost', permission: 'io.post_cost' },
              { text: 'Material Ledger', icon: <ListAltIcon />, path: '/materials/material-ledger', permission: 'mm.view_material' },
            ],
          },
          {
            header: 'Budget Control',
            permission: 'bcs.view_budget',
            items: [
              { text: 'Budget Types', icon: <AssignmentIcon />, path: '/bcs/budget-types', permission: 'bcs.view_budget_type' },
              { text: 'Budget Documents', icon: <ReceiptIcon />, path: '/bcs/budget-documents', permission: 'bcs.view_budget' },
              { text: 'New Transfer', icon: <LinkIcon />, path: '/bcs/budget-documents/transfer/new', permission: 'bcs.create_transfer' },
              { text: 'Cost Center Monthly', icon: <AssignmentIcon />, path: '/bcs/cost-center-monthly', permission: 'bcs.view_budget' },
              { text: 'Project Carry-Forward', icon: <PlayArrowIcon />, path: '/bcs/project-carry-forward', permission: 'bcs.run_carry_forward' },
              { text: 'Dashboard', icon: <TrendingUpIcon />, path: '/bcs/dashboard', permission: 'bcs.view_reports' },
              { text: 'Reports', icon: <DescriptionIcon />, path: '/bcs/reports', permission: 'bcs.view_reports' },
              { text: 'Pivot Analysis', icon: <TrendingUpIcon />, path: '/bcs/pivot', permission: 'bcs.view_reports' },
            ],
          },
          {
            header: 'Asset Accounting',
            permission: 'fi_aa.view_asset',
            items: [
              { text: 'Assets', icon: <AccountBalanceIcon />, path: '/fi-aa/assets', permission: 'fi_aa.view_asset' },
              { text: 'Asset Classes', icon: <AssignmentIcon />, path: '/fi-aa/asset-classes', permission: 'fi_aa.view_asset' },
              { text: 'Depreciation Keys', icon: <TrendingUpIcon />, path: '/fi-aa/depreciation-keys', permission: 'fi_aa.view_asset' },
              { text: 'Acquisitions', icon: <AddIcon />, path: '/fi-aa/acquisitions', permission: 'fi_aa.post_acquisition' },
              { text: 'Asset Receipts', icon: <ReceiptIcon />, path: '/fi-aa/asset-receipts', permission: 'fi_aa.view_asset_receipt' },
              { text: 'Capex on Order', icon: <TrendingUpIcon />, path: '/fi-aa/capex-on-order', permission: 'fi_aa.view_asset' },
              { text: 'Depreciation Runs', icon: <PlayArrowIcon />, path: '/fi-aa/depreciation-runs', permission: 'fi_aa.run_depreciation' },
              { text: 'Income-Tax Depreciation', icon: <AssignmentIcon />, path: '/fi-aa/income-tax-depreciation', permission: 'fi_aa.run_depreciation' },
              { text: 'Opening Balances (Takeover)', icon: <AddIcon />, path: '/fi-aa/opening-balances', permission: 'fi_aa.post_acquisition' },
              { text: 'Transfers', icon: <LinkIcon />, path: '/fi-aa/transfers', permission: 'fi_aa.approve_transfer' },
              { text: 'Disposals', icon: <DescriptionIcon />, path: '/fi-aa/disposals', permission: 'fi_aa.approve_disposal' },
              { text: 'Revaluations', icon: <ReceiptIcon />, path: '/fi-aa/revaluations', permission: 'fi_aa.approve_revaluation' },
              { text: 'Reports', icon: <DashboardIcon />, path: '/fi-aa/reports', permission: 'fi_aa.view_aa_reports' },
            ],
          },
          {
            header: 'GST',
            items: [
              { text: 'GST Compliance', icon: <ReceiptIcon />, path: '/financial/gst-compliance', permission: 'gst.view_einvoice' },
              { text: 'GST Reconciliation', icon: <AssignmentIcon />, path: '/financial/gst-recon', permission: 'fi_gst.view_recon' },
              { text: 'GST Reverse Charge', icon: <DescriptionIcon />, path: '/fi-ap/rcm', permission: 'fi_rcm.view_rcm' },
              { text: 'GST Receipt Voucher', icon: <ReceiptLongIcon />, path: '/financial/gst-receipt-vouchers', permission: 'fi_ar.view_gst_advance' },
              { text: 'GST Refund', icon: <ReceiptLongIcon />, path: '/fi-gst/refund', permission: 'fi_gst.view_refund' },
              { text: 'GST Master', icon: <ReceiptIcon />, path: '/common/gst', permission: 'mdm.view_master_data' },
              { text: 'GST Usage & Cost', icon: <BarChartIcon />, path: '/admin/gst-usage', permission: 'gst.view_usage' },
            ],
          },
        ],
      },
      {
        key: 'messages',
        label: 'Messages',
        icon: <ChatIcon />,
        path: '/messages',
        permission: 'messaging.view_dm',
      },
      {
        key: 'tasks',
        label: 'Tasks Management',
        icon: <TaskIcon />,
        path: '/tasks',
        permission: 'tasks.view_task',
        submenus: [
          { text: 'Projects', icon: <AssignmentIcon />, path: '/tasks/projects' },
          { text: 'Project Chats', icon: <ChatIcon />, path: '/tasks/chat' },
          { text: 'Task Dashboard', icon: <TaskIcon />, path: '/tasks/dashboard' },
          { text: 'Task Worksheet (grid)', icon: <GridOnIcon />, path: '/tasks/excel' },
          { text: 'Tasks Replanning', icon: <EventRepeatIcon />, path: '/tasks/replanning' },
          { text: 'Transfer Employee', icon: <SwapHorizIcon />, path: '/tasks/transfer-employee', permission: 'tasks.change_task' },
          // The same sheet, read-only, for people who want the numbers and the
          // export rather than the editing.
          { text: 'Tasks Report', icon: <AssessmentIcon />, path: '/tasks/report' },
          { text: 'Tasks Upload', icon: <CloudUploadIcon />, path: '/tasks/upload', permission: 'tasks.add_task' },
          { text: 'Task Templates', icon: <DescriptionIcon />, path: '/tasks/templates' },
          { text: 'Task Definitions', icon: <AssignmentIcon />, path: '/tasks/definitions' },
          { text: 'Definition Dependencies', icon: <AccountTreeIcon />, path: '/tasks/definition-dependencies' },
          { text: 'Resource Capacity', icon: <AssessmentIcon />, path: '/tasks/capacity', permission: 'tasks.view_capacity' },
          { text: 'Cost Report', icon: <AssignmentIcon />, path: '/tasks/cost-report' },
        ],
      },
      {
        key: 'workflow',
        label: 'Workflow',
        icon: <AccountTreeIcon />,
        path: '/workflow',
        permission: 'workflow.view_approvals',
        submenus: [
          // Both inboxes ride on the section's own `workflow.view_approvals`,
          // which is IMPLIED by holding any module's approval permission (see
          // usePermissions.js) — an approver must be able to reach the inbox
          // their approvals land in. Both are self-scoped server-side.
          { text: 'My Approvals', icon: <ApprovalIcon />, path: '/workflow/my-approvals' },
          { text: 'Approval Worklist', icon: <PendingActionsIcon />, path: '/workflow/pending-approvals' },
          // Designing workflows is an administration task, not an approver's:
          // it rewrites the rules the approver is subject to.
          { text: 'Manage Workflows', icon: <SettingsIcon />, path: '/workflow/admin/workflows', permission: 'workflow.manage_workflow' },
          { text: 'Notification Settings', icon: <NotificationsIcon />, path: '/workflow/notification-preferences' },
        ],
      },
      {
        key: 'mbe',
        label: 'Exception Cockpit',
        icon: <BugReportIcon />,
        path: '/mbe',
        permission: 'mbe.view_cockpit',
        submenus: [
          { text: 'Cockpit', icon: <DashboardIcon />, path: '/mbe' },
          { text: 'Exceptions', icon: <NotificationImportantIcon />, path: '/mbe/exceptions', permission: 'mbe.view_exception' },
          { text: 'Rules', icon: <SettingsIcon />, path: '/mbe/rules', permission: 'mbe.view_cockpit' },
          { text: 'Analytics', icon: <BarChartIcon />, path: '/mbe/analytics', permission: 'mbe.view_cockpit' },
          { text: 'Suppression Calendar', icon: <CalendarMonthIcon />, path: '/mbe/suppression', permission: 'mbe.manage_rules' },
        ],
      },
      {
        key: 'process-mining',
        label: 'Process Mining',
        icon: <AccountTreeIcon />,
        path: '/process-mining',
        permission: 'pm_mine.view',
        submenus: [
          { text: 'Process Maps', icon: <AccountTreeIcon />, path: '/process-mining', permission: 'pm_mine.view' },
          { text: 'Process Copilot', icon: <PolicyIcon />, path: '/process-copilot', permission: 'copilot.view_finding' },
        ],
      },
      {
        key: 'project-system',
        label: 'Project System',
        icon: <AccountTreeIcon />,
        path: '/projects',
        permission: 'ps.view_project',
        submenus: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/projects/dashboard', permission: 'ps.view_portfolio' },
          { text: 'Projects', icon: <AccountTreeIcon />, path: '/projects', permission: 'ps.view_project' },
          { text: 'Project People', icon: <PeopleIcon />, path: '/projects/resources', permission: 'ps.view_project' },
          { text: 'Project Chat', icon: <ChatIcon />, path: '/projects/chat', permission: 'ps.view_chat' },
          { text: 'Meetings & Minutes', icon: <GroupsIcon />, path: '/projects/meetings', permission: 'ps.view_meeting' },
          { text: 'Timesheets', icon: <CalendarMonthIcon />, path: '/projects/timesheets', permission: 'ps.view_timesheet' },
          { text: 'Time Entry', icon: <MoreTimeIcon />, path: '/projects/timesheets/entry', permission: 'ps.record_time' },
          { text: 'Portfolio', icon: <BarChartIcon />, path: '/projects/portfolio', permission: 'ps.view_portfolio' },
          { text: 'Calendars', icon: <EventAvailableIcon />, path: '/projects/calendars', permission: 'ps.manage_master' },
          { text: 'Project Masters', icon: <TuneIcon />, path: '/projects/masters', permission: 'ps.manage_master' },
        ],
      },
      {
        key: 'mdm',
        label: 'Master Data',
        icon: <StorageIcon />,
        path: '/mdm',
        permissionAny: ['mdm.view_master_data', 'mm.view_inventory', 'qm.view_master_data', 'srm.view_supplier', 'crm.view_lead', 'fi.view_gl_account', 'hr.view_employee'],
        submenus: [
          { text: 'Dashboard', icon: <StorageIcon />, path: '/mdm', permission: 'mdm.view_master_data' },
          { text: 'Currencies', icon: <CurrencyExchangeIcon />, path: '/common/currencies', permission: 'mdm.view_master_data' },
          { text: 'Countries', icon: <PublicIcon />, path: '/common/countries', permission: 'mdm.view_master_data' },
          { text: 'Regions', icon: <MapIcon />, path: '/common/regions', permission: 'mdm.view_master_data' },
          { text: 'Districts', icon: <LocationCityIcon />, path: '/common/districts', permission: 'mdm.view_master_data' },
          { text: 'Payment Terms', icon: <PaymentIcon />, path: '/common/payment-terms', permission: 'mdm.view_master_data' },
          { text: 'Units of Measure', icon: <StraightenIcon />, path: '/common/units-of-measure', permission: 'mdm.view_master_data' },
          { text: 'Seasons', icon: <CalendarMonthIcon />, path: '/common/seasons', permission: 'mdm.view_master_data' },
          { text: 'Warehouses', icon: <StorageIcon />, path: '/master-data/warehouses', permission: 'mm.view_inventory' },
          { text: 'Storage Locations', icon: <InventoryIcon />, path: '/master-data/storage-locations', permission: 'mm.view_inventory' },
          { text: 'Bins', icon: <ViewModuleIcon />, path: '/master-data/bins', permission: 'mm.view_inventory' },
          { text: 'Quality Master Data', icon: <StorageIcon />, path: '/qm/master-data', permission: 'qm.view_master_data' },
          { text: 'Vendor Master', icon: <AssignmentIcon />, path: '/srm/vendor-master', permission: 'srm.view_supplier' },
          { text: 'Customers', icon: <PeopleIcon />, path: '/crm/customers', permission: 'crm.view_lead' },
          { text: 'Company Master', icon: <BusinessIcon />, path: '/financial/companies', permission: 'fi.view_gl_account' },
          { text: 'TDS Sections', icon: <ReceiptLongIcon />, path: '/common/tds-sections', permission: 'mdm.view_master_data' },
          { text: 'Incoterms', icon: <LocalShippingIcon />, path: '/common/incoterms', permission: 'mdm.view_master_data' },
          { text: 'Hierarchy Setup', icon: <AccountTreeIcon />, path: '/common/hierarchy', permission: 'mdm.view_master_data' },
        ],
      },
      {
        key: 'hrm',
        label: 'Human Resources',
        icon: <BadgeIcon />,
        path: '/org-structure',
        // permissionAny, not permission: the HR Dashboard is deliberately
        // grantable WITHOUT the employee master (a CFO wants the cost trend,
        // not everyone's date of birth). With `hr.view_employee` alone on the
        // parent, that grant would give the holder a menu they cannot see, so
        // the section opens for either codename and each child still names its
        // own.
        // `hr.view_separation` is in the list for the same reason
        // `hr.view_dashboard` is: an HR administrator may hold the separation
        // register without the employee master, and with only the first two
        // codenames here that grant would produce a menu they cannot open.
        // `perf.view_own` is in the list because EVERY employee holds it —
        // "My Performance" is a self-service page, and gating the section on
        // `hr.view_employee` would hide an employee's own appraisal from
        // them. That is also why the perf entries below name their own
        // codenames rather than relying on the section.
        permissionAny: [
          'hr.view_employee', 'hr.view_dashboard', 'hr.view_separation',
          'perf.view_own',
          // Employee Relations: a complaint owner or an enquiry officer may
          // hold nothing else in HR, and the section must still open for them.
          'er.view_case', 'er.own_complaint', 'er.view_complaint',
          'er.conduct_enquiry', 'er.report_incident',
          // Objects on Loan: an IT / Stores issuer may hold nothing else in HR.
          'obl.view_record', 'obl.manage_record', 'obl.batch_issue',
          // Employee Loans: Accounts and the DMPC / GM / MD approvers hold
          // loan.* and nothing else in HR.
          'loan.view_loan', 'loan.approve_loan', 'loan.disburse', 'loan.approve_change',
          // Referral Incentives: the Cashier and the GM / MD approvers hold
          // ref.* and nothing else in HR.
          'ref.view_case', 'ref.approve_run', 'ref.release_payment',
          // Statutory Bonus: the Management Auditor, MD, Accounts and Auditor
          // hold bonus.* and nothing else in HR.
          'bonus.view_bonus', 'bonus.view_registers', 'bonus.approve_run', 'bonus.pay_run',
          // Additional Motivation Payment: its roles may hold nothing else in HR.
          'amp.view_amp',
          // Shift & Roster and Payroll were their own sections; their holders
          // may hold nothing else in HR.
          'hrm_tm.view_roster', 'hrm_tm.view_shift',
          'hrm_tm.view_attendance', 'hrm_tm.view_absence',
          'hr.view_payroll',
        ],
        // Grouped by subject, like Accounting. A group's header renders only
        // when at least one of its entries is visible, so a complaint owner
        // sees "Employee Relations" and nothing else. Child nav keys are
        // `hrm:<route>`, independent of the group, so regrouping does not
        // disturb an administrator's page gating.
        submenuGroups: [
          {
            header: 'Core HR',
            items: [
          // Gated on the page's own codename. The ViewSet ORs it with
          // hr.view_employee on reads (every action is a GET), so an existing
          // HR user is not locked out — but the parent menu already requires
          // hr.view_employee, so naming the codename here is what lets an
          // administrator grant the dashboard to somebody who should NOT hold
          // the employee master.
          { text: 'HR Dashboard', icon: <DashboardIcon />, path: '/hrm/dashboard', permission: 'hr.view_dashboard' },
          { text: 'Employee Master', icon: <PeopleIcon />, path: '/hrm/employees' },
          // Separation & exit (G1). Gated on its own codename so an HR
          // administrator can hold the exit register without the whole
          // employee master — and so a manager who holds the master does not
          // silently acquire the exit worklist.
          { text: 'Separations', icon: <ExitToAppIcon />, path: '/hrm/separations', permission: 'hr.view_separation' },
          { text: 'Org & Staffing', icon: <CorporateFareIcon />, path: '/org-structure' },
          { text: 'Hierarchy Setup', icon: <AccountTreeIcon />, path: '/common/hierarchy' },
            ],
          },
          {
            // Shift Rotation & Rostering. Every entry names its own `hrm_tm.*`
            // codename, so a shift supervisor in a spinning mill who holds only
            // the roster sees this group and nothing of the employee master.
            //
            // Prefix `hrm_tm.` — NOT `hr.`. `hr.view_time` is the analytics
            // codename over aggregated absence figures and is held broadly; a
            // roster names which individual works which night.
            header: 'Shift & Roster',
            items: [
          { text: 'Roster Workspace', icon: <CalendarMonthIcon />, path: '/hrm/time/roster', permission: 'hrm_tm.view_roster' },
          { text: 'Coverage & Manning', icon: <GroupsIcon />, path: '/hrm/time/coverage', permission: 'hrm_tm.view_roster' },
          { text: 'Attendance', icon: <FactCheckIcon />, path: '/hrm/time/attendance', permission: 'hrm_tm.view_attendance' },
          // Recording an absence on behalf of an employee. Gated on
          // `hrm_tm.view_absence`, which is its own codename and not
          // `view_attendance`: attendance is what the punches said, an absence
          // is a deduction from somebody's leave and, unpaid, from their pay.
          { text: 'Absence Entry', icon: <EventBusyIcon />, path: '/hrm/time/absences', permission: 'hrm_tm.view_absence' },
          // Short permission. Gated on the SAME codename as the absence
          // register, deliberately: it is the same record and the same act,
          // only denominated in hours. Minting the monthly cap is the one
          // thing that needed its own codename
          // (`hrm_tm.manage_permission_quota`), and that is enforced at the
          // API, not by hiding a menu item.
          { text: 'Permissions', icon: <TimerIcon />, path: '/hrm/time/permissions', permission: 'hrm_tm.view_absence' },
          { text: 'Shift Swaps', icon: <SwapHorizIcon />, path: '/hrm/time/swaps', permission: 'hrm_tm.view_roster' },
          // Master data. Gated on `view_shift` so a supervisor who reads the
          // roster does not silently acquire the shift definitions the whole
          // plant is rostered from.
          { text: 'Shift Master', icon: <ScheduleIcon />, path: '/hrm/time/shifts', permission: 'hrm_tm.view_shift' },
          { text: 'Rotation Patterns', icon: <TuneIcon />, path: '/hrm/time/patterns', permission: 'hrm_tm.view_shift' },
          { text: 'Crews', icon: <GroupsIcon />, path: '/hrm/time/crews', permission: 'hrm_tm.view_shift' },
          // Work schedules (IT0007) — the absence-rate denominator. Gated on
          // `hr.view_time`, NOT an `hrm_tm.*` codename: it is the G5 data the
          // HR Dashboard already reads on that codename (apps/hrm/schedule_api.py),
          // a weekly contract rather than who works which night. Writes take
          // `hr.change_employee`, enforced at the API.
          { text: 'Work Schedules', icon: <CalendarMonthIcon />, path: '/hrm/time/work-schedules', permission: 'hr.view_time' },
          { text: 'Attendance Terminals', icon: <SettingsIcon />, path: '/hrm/time/devices', permission: 'hrm_tm.view_attendance' },
          // The on-premise agents. Its own codename, not `view_attendance`:
          // this screen registers credentials that can write attendance for
          // the whole tenant, which is a larger act than configuring a reader.
          { text: 'Attendance Agents', icon: <SettingsIcon />, path: '/hrm/time/integrations', permission: 'hrm_tm.view_integration' },
          // The statutory thresholds and the night-work exemptions. Reading it
          // rides on `view_roster`; EDITING a threshold takes
          // `hrm_tm.change_compliance_rule`, which should sit with very few
          // people and is enforced at the API.
          { text: 'Compliance', icon: <GavelIcon />, path: '/hrm/time/compliance', permission: 'hrm_tm.view_roster' },
            ],
          },
          {
            // Payroll. The section used to gate every entry on `hr.view_payroll`;
            // inside Human Resources each entry has to name it itself, or it
            // would be visible to anyone who can open HR.
            header: 'Payroll',
            subGroups: [
              {
                header: 'Processing',
                items: [
                { text: 'Cockpit', icon: <DashboardIcon />, path: '/hrm/payroll', permission: 'hr.view_payroll' },
                { text: 'Payroll Runs', icon: <PlayCircleIcon />, path: '/hrm/payroll/runs', permission: 'hr.view_payroll' },
                { text: 'Payroll Results', icon: <AssessmentIcon />, path: '/hrm/payroll/results', permission: 'hr.view_payroll' },
                { text: 'Off-Cycle Payrolls', icon: <PlayCircleIcon />, path: '/hrm/payroll/off-cycle-payrolls', permission: 'hr.view_payroll' },
                { text: 'Off-Cycle Items', icon: <CategoryIcon />, path: '/hrm/payroll/off-cycle-items', permission: 'hr.view_payroll' },
                { text: 'Retro Calculations', icon: <AssessmentIcon />, path: '/hrm/payroll/retro-calculations', permission: 'hr.view_payroll' },
                { text: 'Final Settlements', icon: <AssessmentIcon />, path: '/hrm/payroll/final-settlements', permission: 'hr.view_payroll' },
                { text: 'Recurring Payments', icon: <PaymentsIcon />, path: '/hrm/payroll/recurring-payments', permission: 'hr.view_payroll' },
                { text: 'Additional Payments', icon: <PaymentsIcon />, path: '/hrm/payroll/additional-payments', permission: 'hr.view_payroll' },
                ],
              },
              {
                header: 'Compensation',
                items: [
                { text: 'Compensation Plans', icon: <AccountTreeIcon />, path: '/hrm/payroll/compensation-plans', permission: 'hr.view_payroll' },
                { text: 'Compensation Zones', icon: <CategoryIcon />, path: '/hrm/payroll/compensation-zones', permission: 'hr.view_payroll' },
                { text: 'Compensation Bands', icon: <CategoryIcon />, path: '/hrm/payroll/compensation-bands', permission: 'hr.view_payroll' },
                { text: 'Compensation Steps', icon: <CategoryIcon />, path: '/hrm/payroll/compensation-steps', permission: 'hr.view_payroll' },
                { text: 'Compensation Grid', icon: <CategoryIcon />, path: '/hrm/payroll/compensation-grid', permission: 'hr.view_payroll' },
                { text: 'Comp. Components', icon: <CategoryIcon />, path: '/hrm/payroll/compensation-components', permission: 'hr.view_payroll' },
                { text: 'Employee Compensations', icon: <PeopleIcon />, path: '/hrm/payroll/employee-compensations', permission: 'hr.view_payroll' },
                ],
              },
              {
                header: 'Tax & Statutory',
                items: [
                { text: 'Tax Declarations', icon: <ReceiptIcon />, path: '/hrm/payroll/tax-declarations', permission: 'hr.view_payroll' },
                { text: 'Tax Slabs', icon: <BarChartIcon />, path: '/hrm/payroll/tax-slabs', permission: 'hr.view_payroll' },
                { text: 'Tax Projections', icon: <AssessmentIcon />, path: '/hrm/payroll/tax-projections', permission: 'hr.view_payroll' },
                { text: 'Employee Tax Configs', icon: <ReceiptIcon />, path: '/hrm/payroll/employee-tax-configs', permission: 'hr.view_payroll' },
                { text: 'Statutory Components', icon: <GavelIcon />, path: '/hrm/payroll/statutory-components', permission: 'hr.view_payroll' },
                // Statutory filings (E7). Gated on hrf.view_filing — the
                // filings API checks that codename alone — so a payroll
                // clerk without filing rights does not see a dead menu item.
                { text: 'Statutory Filings', icon: <GavelIcon />, path: '/hrm/payroll/filings', permission: 'hrf.view_filing' },
                { text: 'Filing Calendar', icon: <CalendarMonthIcon />, path: '/hrm/payroll/filings/calendar', permission: 'hrf.view_filing' },
                { text: 'Form 16', icon: <ReceiptIcon />, path: '/hrm/payroll/filings/form16', permission: 'hrf.view_filing' },
                ],
              },
              {
                header: 'Posting & Bank',
                items: [
                { text: 'GL Configuration', icon: <AccountBalanceIcon />, path: '/hrm/payroll/gl-configs', permission: 'hr.view_payroll' },
                { text: 'Posting Logs', icon: <ReceiptIcon />, path: '/hrm/payroll/posting-logs', permission: 'hr.view_payroll' },
                { text: 'Bank File Configs', icon: <AccountBalanceIcon />, path: '/hrm/payroll/bank-file-configs', permission: 'hr.view_payroll' },
                { text: 'Bank File Generations', icon: <DescriptionIcon />, path: '/hrm/payroll/bank-file-generations', permission: 'hr.view_payroll' },
                ],
              },
              {
                header: 'Configuration',
                items: [
                { text: 'Payroll Periods', icon: <CalendarMonthIcon />, path: '/hrm/payroll/periods', permission: 'hr.view_payroll' },
                { text: 'Payroll Areas', icon: <PeopleIcon />, path: '/hrm/payroll/areas', permission: 'hr.view_payroll' },
                { text: 'Area Assignments', icon: <PeopleIcon />, path: '/hrm/payroll/area-assignments', permission: 'hr.view_payroll' },
                { text: 'Wage Types', icon: <CategoryIcon />, path: '/hrm/payroll/wage-types', permission: 'hr.view_payroll' },
                { text: 'Country Config', icon: <SettingsIcon />, path: '/hrm/payroll/country-configs', permission: 'hr.view_payroll' },
                { text: 'Tenant Overrides', icon: <SettingsIcon />, path: '/hrm/payroll/tenant-overrides', permission: 'hr.view_payroll' },
                { text: 'Payslip Templates', icon: <DescriptionIcon />, path: '/hrm/payroll/payslip-templates', permission: 'hr.view_payroll' },
                ],
              },
            ],
          },
          {
            header: 'Recruitment',
            items: [
          // Recruitment (G3), gated on `rec.*` — NOT `hr.*`. The prefix is
          // deliberately distinct because there are two recruitment apps whose
          // labels differ by one character, and a shared prefix would make a
          // mis-granted permission invisible.
          { text: 'Requisitions', icon: <WorkOutlineIcon />, path: '/recruitment/requisitions', permission: 'rec.view_requisition' },
          { text: 'Candidates', icon: <PersonSearchIcon />, path: '/recruitment/candidates', permission: 'rec.view_candidate' },
          // Compensation lives on this page, so it is gated like payroll data
          // rather than like candidate data. An interviewer holding only
          // `rec.submit_feedback` never sees this entry.
          { text: 'Offers', icon: <AssignmentTurnedInIcon />, path: '/recruitment/offers', permission: 'rec.view_offer' },
            ],
          },
          {
            header: 'Performance & Learning',
            items: [
          // Performance & Learning (G4), gated on `perf.*` — NOT `hr.*`. A
          // rating is the most sensitive non-salary datum in the HR estate,
          // and `hr.view_employee` is held by most of the HR estate.
          //
          // "My Performance" takes `perf.view_own`, which every employee
          // holds — it shows only their own record, narrowed on the server.
          // "Appraisal Cycles" takes `perf.view_reports`: cycle dates and
          // progress counts carry no individual rating, and the actions that
          // change a cycle take `perf.manage_cycle` at the API.
          { text: 'My Performance', icon: <EmojiEventsIcon />, path: '/performance/me', permission: 'perf.view_own' },
          { text: 'Goals', icon: <FlagIcon />, path: '/performance/goals', permission: 'perf.view_goal' },
          { text: 'Goal & Appraisal Setup', icon: <TuneIcon />, path: '/performance/goal-setup', permission: 'perf.manage_template' },
          // Phase 3. Both take their OWN codename rather than riding on an
          // existing one: maintaining the competency framework is not the same
          // authority as assessing somebody against it, and a calibration
          // session lists a whole population's ratings side by side BY NAME —
          // a wider disclosure than any single appraisal.
          { text: 'Competency Framework', icon: <PsychologyIcon />, path: '/performance/competencies', permission: 'perf.manage_competency' },
          { text: 'Calibration', icon: <BalanceIcon />, path: '/performance/calibration', permission: 'perf.run_calibration' },
          // Phase 4. "My 360" is on `perf.view_own` because every employee is
          // either a rater or a subject; running campaigns is its own codename
          // because it exposes every subject's aggregate BY NAME.
          { text: 'My 360° Feedback', icon: <GroupsIcon />, path: '/performance/my-360', permission: 'perf.view_own' },
          { text: '360° Campaigns', icon: <PollIcon />, path: '/performance/360', permission: 'perf.manage_360' },
          // Phase 5. There is deliberately NO self-service counterpart to the
          // 9-box: T-3 says no employee is shown their own cell by the system,
          // which is the one inversion in this module — everywhere else the
          // subject is the most entitled reader of their own record.
          // `perf.view_potential` does not ride on `perf.view_team`; holding
          // it is what says this manager may see the talent view.
          { text: 'Talent Grid (9-Box)', icon: <GridViewIcon />, path: '/performance/nine-box', permission: 'perf.view_potential' },
          // One codename covers reading and maintaining a bench: the sensitive
          // content of a succession plan IS the list of names, so a read-only
          // grant would exist only to widen the audience T-7 protects.
          { text: 'Succession', icon: <AltRouteIcon />, path: '/performance/succession', permission: 'perf.manage_succession' },
          { text: 'Development Plans', icon: <TrendingUpIcon />, path: '/performance/development', permission: 'perf.view_idp' },
          { text: 'Appraisal Cycles', icon: <AssessmentIcon />, path: '/performance/cycles', permission: 'perf.view_reports' },
          { text: 'Training & Compliance', icon: <SchoolIcon />, path: '/performance/training', permission: 'perf.view_training' },
            ],
          },
          {
            header: 'Employee Relations',
            items: [
          // Employee Relations (DEV-10), gated on `er.*`. The register takes
          // `er.view_case`; the complaints register opens for the HR Manager,
          // the register viewer AND a complaint owner (welfare / safety /
          // compliance officer), because the owner works assigned complaints
          // from the same page — the server narrows the rows to theirs.
          { text: 'Disciplinary Cases', icon: <GavelIcon />, path: '/employee-relations/cases', permissionAny: ['er.view_case', 'er.report_incident', 'er.conduct_enquiry', 'er.approve_case'] },
          { text: 'Complaints', icon: <ReportProblemIcon />, path: '/employee-relations/complaints', permissionAny: ['er.view_complaint', 'er.manage_complaint', 'er.own_complaint'] },
          { text: 'ER Reports', icon: <AssessmentIcon />, path: '/employee-relations/reports', permission: 'er.view_reports' },
          { text: 'ER Setup', icon: <TuneIcon />, path: '/employee-relations/setup', permission: 'er.manage_master' },
            ],
          },
          {
            header: 'Objects on Loan',
            items: [
          // Objects on Loan (DEV-11), gated on `obl.*`. The register opens
          // for every issuer role (the server narrows to their owner role);
          // recoveries for the HR Manager and Payroll; setup for the master
          // holder only.
          { text: 'Objects on Loan', icon: <InventoryIcon />, path: '/objects-on-loan/register', permissionAny: ['obl.view_record', 'obl.manage_record', 'obl.batch_issue', 'obl.approve_recovery'] },
          { text: 'Object Recoveries', icon: <CurrencyRupeeIcon />, path: '/objects-on-loan/recoveries', permissionAny: ['obl.approve_recovery', 'obl.view_reports', 'obl.view_record'] },
          { text: 'Objects Reports', icon: <AssessmentIcon />, path: '/objects-on-loan/reports', permissionAny: ['obl.view_reports', 'obl.view_record'] },
          { text: 'Objects Setup', icon: <TuneIcon />, path: '/objects-on-loan/setup', permission: 'obl.manage_master' },
            ],
          },
          {
            header: 'Employee Loans',
            items: [
          // Employee Loans & Advances (DEV-03), gated on `loan.*`. The
          // register opens for every role in the matrix; approvals for the
          // level approvers and Accounts; setup for the master holder only.
          { text: 'Employee Loans', icon: <CurrencyRupeeIcon />, path: '/employee-loans/register', permissionAny: ['loan.view_loan', 'loan.manage_loan', 'loan.approve_loan', 'loan.disburse', 'loan.approve_change'] },
          { text: 'Loan Approvals', icon: <VerifiedUserIcon />, path: '/employee-loans/approvals', permissionAny: ['loan.approve_loan', 'loan.approve_change', 'loan.write_off', 'loan.manage_loan'] },
          { text: 'Loan Reports', icon: <AssessmentIcon />, path: '/employee-loans/reports', permissionAny: ['loan.view_reports', 'loan.view_loan'] },
          { text: 'Loans Setup', icon: <TuneIcon />, path: '/employee-loans/setup', permission: 'loan.manage_master' },
            ],
          },
          {
            header: 'Referral Incentives',
            items: [
          // Referral Incentive Scheme (DEV-01), gated on `ref.*`. Cases for
          // every role in the §10 matrix; runs for the approvers, the Cashier
          // and Payroll; schemes for the master holder only.
          { text: 'Referral Cases', icon: <PeopleIcon />, path: '/referral-incentives/cases', permissionAny: ['ref.view_case', 'ref.manage_case', 'ref.approve_case'] },
          { text: 'Commission Runs', icon: <CurrencyRupeeIcon />, path: '/referral-incentives/runs', permissionAny: ['ref.manage_run', 'ref.approve_run', 'ref.release_payment', 'ref.view_case'] },
          { text: 'Referral Reports', icon: <AssessmentIcon />, path: '/referral-incentives/reports', permissionAny: ['ref.view_reports', 'ref.view_ledger', 'ref.view_case'] },
          { text: 'Referral Schemes', icon: <TuneIcon />, path: '/referral-incentives/schemes', permission: 'ref.manage_scheme' },
            ],
          },
          {
            header: 'Statutory Bonus',
            items: [
          // Statutory Bonus Engine (DEV-02), gated on `bonus.*`. Runs for every
          // role of the §10 matrix; registers for their holders; setup for the
          // configuration / wage-flag / carry-in holders.
          { text: 'Bonus Runs', icon: <CurrencyRupeeIcon />, path: '/statutory-bonus/runs', permissionAny: ['bonus.view_bonus', 'bonus.process_run', 'bonus.check_run', 'bonus.approve_run', 'bonus.pay_run'] },
          { text: 'Bonus Reports & Registers', icon: <AssessmentIcon />, path: '/statutory-bonus/reports', permissionAny: ['bonus.view_bonus', 'bonus.view_registers'] },
          { text: 'Bonus Setup', icon: <TuneIcon />, path: '/statutory-bonus/setup', permissionAny: ['bonus.manage_config', 'bonus.manage_wage_flags', 'bonus.process_run', 'bonus.disqualify', 'bonus.close_year'] },
            ],
          },
          {
            header: 'Additional Motivation',
            items: [
          // Additional Motivation Payment (DEV-04). `ownPermissionAny`, not
          // `permissionAny`: the tenant-admin bypass does NOT apply (BR-44) —
          // an administrator sees these only by holding an AMP role.
          { text: 'Additional Motivation', icon: <CurrencyRupeeIcon />, path: '/additional-motivation/runs', ownPermissionAny: ['amp.view_amp'] },
          { text: 'AMP Hours Split', icon: <AssessmentIcon />, path: '/additional-motivation/hours', ownPermissionAny: ['amp.view_amp'] },
          { text: 'AMP Other Earnings', icon: <AssessmentIcon />, path: '/additional-motivation/other-earnings', ownPermissionAny: ['amp.process_run'] },
          { text: 'AMP Setup', icon: <TuneIcon />, path: '/additional-motivation/setup', ownPermissionAny: ['amp.manage_config'] },
            ],
          },
        ],
      },
      {
        key: 'travel',
        label: 'Travel Management',
        icon: <FlightTakeoffIcon />,
        path: '/hrm/travel/requests',
        permission: 'travel.view_request',
        submenus: [
          { text: 'Travel Requests', icon: <FlightTakeoffIcon />, path: '/hrm/travel/requests' },
          { text: 'Expense Claims', icon: <ReceiptIcon />, path: '/hrm/travel/claims' },
          { text: 'Travel Advances', icon: <AccountBalanceIcon />, path: '/hrm/travel/advances' },
          { text: 'Travel Desk', icon: <WorkIcon />, path: '/hrm/travel/desk' },
          { text: 'Travel Policies', icon: <PolicyIcon />, path: '/hrm/travel/policies' },
          { text: 'Travel Vendors', icon: <BusinessIcon />, path: '/hrm/travel/vendors' },
          { text: 'Expense Categories', icon: <CategoryIcon />, path: '/hrm/travel/expense-categories' },
          { text: 'Destination Tiers', icon: <LocationOnIcon />, path: '/hrm/travel/destination-tiers' },
        ],
      },
      {
        key: 'rem',
        label: 'Real Estate',
        icon: <ApartmentIcon />,
        path: '/rem',
        // permissionAny, not permission: a hostel warden or Security Officer
        // (DEV-09) holds `hostel.*` and nothing in Real Estate. Each Property &
        // Leasing entry now carries the `rem.view_property` the section used to.
        permissionAny: [
          'rem.view_property', 'hostel.view_hostel', 'hostel.record_movement',
          'hostel.manage_master', 'hostel.raise_request', 'hostel.run_charges',
        ],
        submenuGroups: [
          {
            header: 'Property & Leasing',
            items: [
              { text: 'Dashboard', icon: <ApartmentIcon />, path: '/rem', permission: 'rem.view_property' },
              { text: 'Properties', icon: <ApartmentIcon />, path: '/rem/properties', permission: 'rem.view_property' },
              { text: 'Property Map', icon: <LocationOnIcon />, path: '/rem/property-map', permission: 'rem.view_property' },
              { text: 'Property Types', icon: <CategoryIcon />, path: '/rem/property-types', permission: 'rem.view_property' },
              { text: 'Property Statuses', icon: <FactCheckIcon />, path: '/rem/property-statuses', permission: 'rem.view_property' },
              { text: 'Property SF Numbers', icon: <ArticleIcon />, path: '/rem/property-sf-numbers', permission: 'rem.view_property' },
              { text: 'Property Documents', icon: <FolderIcon />, path: '/rem/property-documents', permission: 'rem.view_property' },
              { text: 'Property Ownerships', icon: <AssignmentIcon />, path: '/rem/property-ownerships', permission: 'rem.view_property' },
              { text: 'Property Valuations', icon: <BarChartIcon />, path: '/rem/property-valuations', permission: 'rem.view_property' },
              { text: 'Property Taxes', icon: <ReceiptIcon />, path: '/rem/property-taxes', permission: 'rem.view_property' },
              { text: 'Property Insurances', icon: <VerifiedUserIcon />, path: '/rem/property-insurances', permission: 'rem.view_property' },
              { text: 'Property Expenses', icon: <PaymentIcon />, path: '/rem/property-expenses', permission: 'rem.view_property' },
              { text: 'Shared Documents', icon: <ShareIcon />, path: '/rem/shared-documents', permission: 'rem.view_property' },
              { text: 'Floors', icon: <LayersIcon />, path: '/rem/floors', permission: 'rem.view_property' },
              { text: 'Spaces', icon: <SpaceDashboardIcon />, path: '/rem/spaces', permission: 'rem.view_property' },
              { text: 'Space Types', icon: <CategoryIcon />, path: '/rem/space-types', permission: 'rem.view_property' },
              { text: 'Space Amenities', icon: <StarIcon />, path: '/rem/space-amenities', permission: 'rem.view_property' },
              { text: 'Space Modifications', icon: <BuildIcon />, path: '/rem/space-modifications', permission: 'rem.view_property' },
              { text: 'Space Occupancies', icon: <PeopleIcon />, path: '/rem/space-occupancies', permission: 'rem.view_property' },
              { text: 'Common Areas', icon: <MeetingRoomIcon />, path: '/rem/common-areas', permission: 'rem.view_property' },
              { text: 'Tenants', icon: <PeopleIcon />, path: '/rem/tenants', permission: 'rem.view_property' },
              { text: 'Tenant Types', icon: <CategoryIcon />, path: '/rem/tenant-types', permission: 'rem.view_property' },
              { text: 'Tenant Contacts', icon: <ContactPhoneIcon />, path: '/rem/tenant-contacts', permission: 'rem.view_property' },
              { text: 'Tenant Addresses', icon: <LocationOnIcon />, path: '/rem/tenant-addresses', permission: 'rem.view_property' },
              { text: 'Tenant Screenings', icon: <FactCheckIcon />, path: '/rem/tenant-screenings', permission: 'rem.view_property' },
              { text: 'Tenant Approvals', icon: <HowToRegIcon />, path: '/rem/tenant-approvals', permission: 'rem.view_property' },
              { text: 'Tenant Communications', icon: <ChatIcon />, path: '/rem/tenant-communications', permission: 'rem.view_property' },
              { text: 'Tenant Documents', icon: <FolderIcon />, path: '/rem/tenant-documents', permission: 'rem.view_property' },
              { text: 'Tenant Histories', icon: <HistoryIcon />, path: '/rem/tenant-histories', permission: 'rem.view_property' },
              { text: 'Tenant Incidents', icon: <ReportProblemIcon />, path: '/rem/tenant-incidents', permission: 'rem.view_property' },
              { text: 'Tenant Notifications', icon: <NotificationsActiveIcon />, path: '/rem/tenant-notifications', permission: 'rem.view_property' },
              { text: 'Leases', icon: <DescriptionIcon />, path: '/rem/leases', permission: 'rem.view_property' },
              { text: 'Lease Types', icon: <CategoryIcon />, path: '/rem/lease-types', permission: 'rem.view_property' },
              { text: 'Lease Renewal Offers', icon: <AssignmentIcon />, path: '/rem/lease-renewal-offers', permission: 'rem.view_property' },
              { text: 'Lease Terminations', icon: <GavelIcon />, path: '/rem/lease-terminations', permission: 'rem.view_property' },
              { text: 'Rent Payments', icon: <PaymentIcon />, path: '/rem/rent-payments', permission: 'rem.view_property' },
              { text: 'Utility Charges', icon: <ElectricBoltIcon />, path: '/rem/utility-charges', permission: 'rem.view_property' },
              { text: 'Maintenance', icon: <BuildIcon />, path: '/rem/maintenance', permission: 'rem.view_property' },
              { text: 'Maintenance Activities', icon: <ConstructionIcon />, path: '/rem/maintenance-activities', permission: 'rem.view_property' },
              { text: 'Inspection Schedules', icon: <EventNoteIcon />, path: '/rem/inspection-schedules', permission: 'rem.view_property' },
              { text: 'Inspection Reports', icon: <AssignmentIcon />, path: '/rem/inspection-reports', permission: 'rem.view_property' },
              { text: 'Rent Roll', icon: <TableChartIcon />, path: '/rem/reports/rent-roll', permission: 'rem.view_property' },
              { text: 'Lease Expiry', icon: <EventBusyIcon />, path: '/rem/reports/lease-expiry', permission: 'rem.view_property' },
              { text: 'Rent Arrears', icon: <MoneyOffIcon />, path: '/rem/reports/rent-arrears', permission: 'rem.view_property' },
              { text: 'Occupancy & Vacancy', icon: <SpaceDashboardIcon />, path: '/rem/reports/occupancy', permission: 'rem.view_property' },
              { text: 'Income & Expense', icon: <AssessmentIcon />, path: '/rem/reports/income-expense', permission: 'rem.view_property' },
              { text: 'Tenant Ledger', icon: <ReceiptLongIcon />, path: '/rem/reports/tenant-ledger', permission: 'rem.view_property' },
              { text: 'Module Settings', icon: <SettingsIcon />, path: '/rem/settings', permission: 'rem.view_property' },
            ],
          },
          {
            header: 'Hostel',
            items: [
              // Hostel Management (DEV-09), gated on `hostel.*`. A warden sees own
              // hostels only (the warden roster, on the server); the Security
              // Officer holds `hostel.record_movement` alone and gets the gate.
              { text: 'Hostel Dashboard', icon: <HotelIcon />, path: '/hostel/dashboard', permissionAny: ['hostel.view_hostel', 'hostel.manage_master', 'hostel.raise_request', 'hostel.decide_request', 'hostel.manage_residency'] },
              { text: 'Hostel Residents', icon: <HotelIcon />, path: '/hostel/residents', permissionAny: ['hostel.view_hostel', 'hostel.manage_residency', 'hostel.raise_request'] },
              { text: 'Hostel Requests', icon: <HotelIcon />, path: '/hostel/requests', permissionAny: ['hostel.raise_request', 'hostel.decide_request', 'hostel.manage_residency', 'hostel.view_hostel'] },
              { text: 'Hostel Gate', icon: <HotelIcon />, path: '/hostel/movement', permissionAny: ['hostel.record_movement', 'hostel.manage_residency'] },
              { text: 'Night Strength', icon: <HotelIcon />, path: '/hostel/strength', permissionAny: ['hostel.confirm_strength', 'hostel.view_hostel'] },
              { text: 'Hostel Visitors', icon: <HotelIcon />, path: '/hostel/visits', permissionAny: ['hostel.manage_residency', 'hostel.record_movement', 'hostel.view_hostel'] },
              { text: 'Hostel Incidents', icon: <HotelIcon />, path: '/hostel/incidents', permissionAny: ['hostel.record_incident', 'hostel.decide_recovery', 'hostel.execute_order', 'hostel.view_hostel'] },
              { text: 'Hostel Charges', icon: <CurrencyRupeeIcon />, path: '/hostel/charges', permission: 'hostel.run_charges' },
              { text: 'Hostel Reports', icon: <AssessmentIcon />, path: '/hostel/reports', permissionAny: ['hostel.view_reports', 'hostel.view_hostel', 'hostel.run_charges'] },
              { text: 'Hostel Setup', icon: <TuneIcon />, path: '/hostel/setup', permission: 'hostel.manage_master' },
            ],
          },
        ],
      },
      {
        key: 'investment',
        label: 'Investment Portfolio',
        icon: <ShowChartIcon />,
        path: '/investment',
        permission: 'inv.view_investment',
        submenus: [
          { text: 'Portfolio Dashboard', icon: <ShowChartIcon />, path: '/investment/overview' },
          { text: 'Holdings', icon: <AccountBalanceIcon />, path: '/investment/holdings' },
          { text: 'Transactions', icon: <SwapHorizIcon />, path: '/investment/transactions' },
          { text: 'Securities', icon: <BarChartIcon />, path: '/investment/securities' },
        ],
      },
    ],
  },
  {
    id: 'system',
    items: [
      {
        // Data Migration is its own group: uploading data and AUTHORING the
        // template that governs it are different jobs for different people,
        // and the template editor decides which table a spreadsheet may write
        // to. Grouping them under "AI" hid that distinction.
        key: 'data-migration',
        label: 'Data Migration',
        icon: <CloudUploadIcon />,
        path: '/data-migration',
        // Running an upload writes into the table its template targets, so the
        // job API takes `app.run_data_upload` (2026-10-14; it was open to every
        // login). Authoring a template takes `app.configuration`.
        submenus: [
          { text: 'Data Upload', icon: <CloudUploadIcon />, path: '/data-migration', permission: 'app.run_data_upload' },
          { text: 'Upload Templates', icon: <GridOnIcon />, path: '/data-migration/templates', permission: 'app.configuration' },
        ],
      },
      {
        key: 'ai',
        label: 'AI',
        icon: <AutoFixHighIcon />,
        path: '/ask-biztimize',
        permissionAny: ['ai.use_chat', 'ai.opening_inventory', 'ai.price_list_import', 'app.configuration', 'tenant.admin', 'crm.view_sales_order', 'qm.view_master_data'],
        submenus: [
          { text: 'Ask Biztimize', icon: <ChatIcon />, path: '/ask-biztimize', permission: 'ai.use_chat' },
          { text: 'Customer PO Import', icon: <CloudUploadIcon />, path: '/crm/po-import', permission: 'crm.view_sales_order' },
          { text: 'Opening Inventory Upload', icon: <CloudUploadIcon />, path: '/materials/opening-inventory', permission: 'ai.opening_inventory' },
          { text: 'Measurement Chart Import', icon: <CloudUploadIcon />, path: '/quality/measurement-charts', permission: 'qm.view_master_data' },
          { text: 'Selling Price Upload', icon: <CloudUploadIcon />, path: '/pos/price-list-upload', permission: 'ai.price_list_import' },
          { text: 'AI Data Seeding', icon: <AutoFixHighIcon />, path: '/admin/data-seeding', permission: 'app.configuration' },
          { text: 'AI Usage & Cost', icon: <BarChartIcon />, path: '/admin/ai-usage', permission: 'tenant.admin' },
        ],
      },
      {
        key: 'admin',
        label: 'Settings',
        icon: <AdminPanelSettingsIcon />,
        path: '/admin',
        // permissionAny, not permission: a Print Format Administrator holds
        // print.view_form but not tenant.admin, and a group gated on
        // tenant.admin alone would hide the only screen their permission
        // reaches. Each submenu still carries its own gate.
        // The same applies to the Inventory Accounting Configurator: they hold
        // the two posting-setup pairs and nothing else in this group, and both
        // of their screens live here.
        permissionAny: ['tenant.admin', 'print.view_form', 'email.view_channel',
                        'mm.view_posting_setup', 'mm.view_service_accounting',
                        'numbering.view_range', 'fieldstatus.view_variant'],
        submenus: [
          { text: 'System Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', permission: 'tenant.admin' },
          { text: 'User & Session Monitoring', icon: <PeopleIcon />, path: '/admin/dashboard', permission: 'tenant.admin' },
          { text: 'License & Subscription', icon: <VerifiedUserIcon />, path: '/admin/license', permission: 'tenant.admin' },
          { text: 'PO/PR Numbering', icon: <SettingsIcon />, path: '/srm/numbering-sequences', permission: 'mm.manage_po_numbering' },
          { text: 'Inventory Posting Setup', icon: <SettingsIcon />, path: '/materials/posting-setup', permission: 'mm.view_posting_setup' },
          { text: 'Service Accounting Setup', icon: <SettingsIcon />, path: '/materials/service-accounting', permission: 'mm.view_service_accounting' },
          { text: 'Print Formats', icon: <DescriptionIcon />, path: '/print-forms', permission: 'print.view_form' },
          { text: 'Email Channels', icon: <EmailIcon />, path: '/settings/email-channels', permission: 'email.view_channel' },
          { text: 'Document Numbering', icon: <SettingsIcon />, path: '/settings/document-numbering', permission: 'numbering.view_range' },
          { text: 'Field Status', icon: <SettingsIcon />, path: '/settings/field-status', permission: 'fieldstatus.view_variant' },
          { text: 'Writing Assistant', icon: <SettingsIcon />, path: '/settings/writing-assistant', permission: 'wa.view_config' },
        ],
      },
      {
        key: 'administrator',
        label: 'Administrator',
        icon: <AdminPanelSettingsIcon />,
        path: '/administrator/audit-log',
        // permissionAny, not permission: the Operations Console is gated on
        // ops.view_command, and a parent hard-gated on tenant.admin would make
        // that grant unreachable — every child is still filtered on its own
        // codename, so this exposes the console entry and nothing else.
        permissionAny: ['tenant.admin', 'ops.view_command', 'explorer.view_table', 'backup.view_backup', 'aikeys.view_key', 'pp.manage_ecm_config'],
        submenus: [
          { text: 'Audit Log', icon: <HistoryIcon />, path: '/administrator/audit-log', permission: 'tenant.admin' },
          { text: 'Operations Console', icon: <TerminalIcon />, path: '/admin/ops-console', permission: 'ops.view_command' },
          { text: 'Table Explorer', icon: <TableChartIcon />, path: '/administrator/table-explorer', permission: 'explorer.view_table' },
          { text: 'Backup & DB Cockpit', icon: <BackupIcon />, path: '/administrator/backups', permission: 'backup.view_backup' },
          { text: 'Change Management Settings', icon: <ChangeCircleIcon />, path: '/settings/change-management', permission: 'pp.manage_ecm_config' },
          { text: 'AI Provider Keys', icon: <VpnKeyIcon />, path: '/administrator/ai-keys', permission: 'aikeys.view_key' },
          { text: 'Bundle Label Printer', icon: <PrintIcon />, path: '/administrator/bundle-label-printer', permission: 'tenant.admin' },
          { text: 'Asset Tag Printer', icon: <QrCode2Icon />, path: '/settings/asset-tag-printer', permission: 'tenant.admin' },
          // Column captions for every screen's grids (GatedGrid applies them). Under
          // /settings: Django's catch-all skips every path starting with "admin", so an
          // /administrator/... route would not survive a hard refresh in prd.
          { text: 'Report Field Labels', icon: <DescriptionIcon />, path: '/settings/report-field-labels', permission: 'tenant.admin' },
        ],
      },
      {
        key: 'user-management',
        label: 'User Management',
        icon: <PeopleIcon />,
        path: '/admin/users',
        permission: 'user.manage',
        submenus: [
          { text: 'Users', icon: <PeopleIcon />, path: '/admin/users', permission: 'user.manage' },
          { text: 'Employee & User Assignment', icon: <PeopleIcon />, path: '/admin/employee-user-assignment', permission: 'user.manage' },
          { text: 'User Preferences', icon: <SettingsIcon />, path: '/admin/user-preferences', permission: 'user.manage' },
          { text: 'RBAC (Roles & Permissions)', icon: <VerifiedUserIcon />, path: '/admin/rbac', permission: 'user.manage' },
          { text: 'Data Access (Company/Plant/Dept)', icon: <VerifiedUserIcon />, path: '/admin/data-access', permission: 'authz.manage' },
          { text: 'Parameter Definitions', icon: <AdminPanelSettingsIcon />, path: '/user-defaults/definitions', permission: 'defaults.view_definition' },
          { text: 'Role Defaults', icon: <AdminPanelSettingsIcon />, path: '/user-defaults/role', permission: 'defaults.manage_role_default' },
          { text: 'Department / Company Defaults', icon: <AccountTreeIcon />, path: '/user-defaults/department', permission: 'defaults.manage_dept_default' },
          { text: 'Defaults Resolution Viewer', icon: <DescriptionIcon />, path: '/user-defaults/resolution' },
        ],
      },
      {
        key: 'help-support',
        label: 'Help & Support',
        icon: <HelpOutlineIcon />,
        path: '/support-desk',
        submenus: [
          { text: 'Support Dashboard', icon: <SupportAgentIcon />, path: '/support-desk' },
          { text: 'My Tickets', icon: <SupportAgentIcon />, path: '/support-desk/tickets' },
          { text: 'Biztimize Notes', icon: <HelpOutlineIcon />, path: '/notes/', sso: true },
          { text: 'Note Assistant', icon: <HelpOutlineIcon />, path: '/knowledge/assistant', permission: 'kb.view_note_assistant' },
          { text: 'Help Center', icon: <HelpOutlineIcon />, path: '/help/', sso: true },
          { text: 'UAT Test Scripts', icon: <HelpOutlineIcon />, path: '/uat/', sso: true },
          { text: 'UAT Progress & Execution', icon: <HelpOutlineIcon />, path: '/uat-testing', permission: 'uat.view_execution' },
        ],
      },
    ],
  },
];

const HeaderSearchContainer = styled('div')(({ theme }) => ({
  width: 320,
  [theme.breakpoints.down('sm')]: { width: 200 },
}));

// ── Supplier Portal sidebar (shown when path starts with /supplier-portal) ──
const supplierPortalSections = [
  {
    id: 'sp-overview',
    items: [
      { key: 'sp-dashboard', label: 'Overview', icon: <DashboardIcon />, path: '/supplier-portal/dashboard' },
    ],
  },
  {
    id: 'sp-main',
    header: 'MY COMPANY',
    items: [
      { key: 'sp-profile',    label: 'Company Profile',    icon: <BusinessIcon />,       path: '/supplier-portal/profile' },
      { key: 'sp-documents',  label: 'Compliance Docs',    icon: <DescriptionIcon />,    path: '/supplier-portal/documents' },
      { key: 'sp-catalog',    label: 'Catalog',            icon: <CategoryIcon />,       path: '/supplier-portal/catalog' },
      { key: 'sp-financials', label: 'Financial Statements', icon: <AccountBalanceIcon />, path: '/supplier-portal/financials' },
      { key: 'sp-connections', label: 'Buyer Connections', icon: <HandshakeIcon />,      path: '/supplier-portal/connections' },
      { key: 'sp-messages',   label: 'Messages',          icon: <ChatIcon />,            path: '/supplier-portal/messages' },
    ],
  },
  {
    id: 'sp-transactions',
    header: 'TRANSACTIONS',
    items: [
      { key: 'sp-rfqs',     label: 'RFQs',           icon: <RequestQuoteIcon />,  path: '/supplier-portal/rfqs' },
      { key: 'sp-orders',   label: 'Purchase Orders', icon: <AssignmentIcon />,   path: '/supplier-portal/purchase-orders' },
      { key: 'sp-shipments',label: 'Shipments',       icon: <LocalShippingIcon />, path: '/supplier-portal/shipments' },
      { key: 'sp-invoices', label: 'Invoices',        icon: <ReceiptIcon />,      path: '/supplier-portal/invoices' },
      { key: 'sp-payments', label: 'Payments',        icon: <PaymentIcon />,      path: '/supplier-portal/payments' },
    ],
  },
  {
    id: 'sp-insights',
    header: 'INSIGHTS',
    items: [
      { key: 'sp-analytics', label: 'Analytics',     icon: <AssessmentIcon />,  path: '/supplier-portal/analytics' },
      { key: 'sp-reviews',   label: 'Reviews',       icon: <StarIcon />,        path: '/supplier-portal/reviews' },
      { key: 'sp-discovery', label: 'Find Buyers',   icon: <SearchIcon />,      path: '/supplier-portal/buyer-discovery' },
    ],
  },
  {
    id: 'sp-account',
    header: 'ACCOUNT',
    items: [
      { key: 'sp-subscription', label: 'Subscription & Billing', icon: <PaymentIcon />, path: '/supplier-portal/subscription' },
    ],
  },
  {
    id: 'sp-support',
    items: [
      { key: 'sp-support', label: 'Help & Support', icon: <HelpOutlineIcon />, path: '/support-desk' },
    ],
  },
];

// ── Navigation registry keys (RBAC Phase 2) ─────────────────────────────────
// Every submenu entry above needs the SAME key `scripts/extract_navigation.py`
// gave it, or the administrator's gating in /admin/rbac → Pages would not find
// it (and it would silently fall back to the hardcoded permission). Built once
// from the constant arrays; children are numbered per parent so a route that
// appears twice under one menu stays distinguishable.
const CHILD_NAV_KEYS = (() => {
  const map = new Map();
  [sidebarSections, supplierPortalSections].forEach((sections) => {
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        // Order matters — it must match the extractor: submenus, then each
        // group's own items, then that group's sub-groups.
        const children = [...(item.submenus || [])];
        (item.submenuGroups || []).forEach((group) => {
          (group.items || []).forEach((c) => children.push(c));
          (group.subGroups || []).forEach((sg) => (sg.items || []).forEach((c) => children.push(c)));
        });
        buildChildKeys(item.key, children).forEach((key, child) => map.set(child, key));
      });
    });
  });
  return map;
})();

// Exported for the module launchers (e.g. pages/financial/AccountingBentoMenu),
// which build their tiles from this same menu definition and reuse CHILD_NAV_KEYS
// so their gating agrees with the sidebar's.
//
// NOTE: these are exported HERE and not with `export const` above, because
// `scripts/extract_navigation.py` greps for the literal `const sidebarSections = [`
// and an `export` prefix makes the fixture extraction fail.
export { sidebarSections, CHILD_NAV_KEYS };

const ModernMainLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [appToasts, setAppToasts] = useState([]);        // [{ id, title, message, appType, url }]
  const [appNotifCount, setAppNotifCount] = useState(0); // unread VMS/FD notification count
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const saveFavoritesTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { status: companyStatus } = useActiveCompany();
  const companyReady = companyStatus === 'ready' || companyStatus === 'error';
  useEffect(() => {
    if (companyStatus === 'idle') loadActiveCompany(api);
  }, [companyStatus]);
  const unreadCount = useSelector(selectUnreadCount);
  const { hasPermission, isTenantAdmin, getPermissions, isLicensed } = usePermissions();
  // Feature licensing: an entry whose module the tenant has not bought is
  // hidden for EVERYONE, tenant admins included — so it is tested outside the
  // admin shortcut and outside the navigation registry, which knows nothing of
  // licences. An entry gated by `permissionAny` stays if any one is licensed.
  const isEntryLicensed = useCallback((entry) => {
    if (entry.permissionAny) return entry.permissionAny.some((p) => isLicensed(p));
    if (entry.ownPermissionAny) return entry.ownPermissionAny.some((p) => isLicensed(p));
    return !entry.permission || isLicensed(entry.permission);
  }, [isLicensed]);
  // Entries that carry `ownPermissionAny` skip the tenant-admin bypass: the
  // user must hold one of the codenames themselves (AMP, FS DEV-04 BR-44).
  const holdsOwn = useCallback(
    (codes) => (codes || []).some((c) => (getPermissions() || []).includes(c)),
    [getPermissions],
  );

  // ── Page gating (RBAC Phase 2) ────────────────────────────────────────────
  // The registry decides when it knows the entry; otherwise the compiled
  // permission below decides, exactly as it did before Phase 2. An entry the
  // registry has never heard of must NOT become visible to everyone, which is
  // why `allows()` takes the old check as its fallback rather than defaulting
  // to true. The API remains authoritative for the data either way.
  const navGating = useNavigationGating();
  // A tenant's own name for a menu entry (Administration → RBAC → Pages →
  // Display name) — e.g. "Gate Entry" for Front Desk. Data, not code: the
  // compiled label is the fallback, so an unseeded registry changes nothing.
  // Favourites store the label they were starred with, so they are re-read
  // from `allMenuItems` by path rather than shown as saved.
  const itemLabel = useCallback((item) => navGating.labelFor(item.key, item.label), [navGating]);
  const subLabel = useCallback((sub) => navGating.labelFor(CHILD_NAV_KEYS.get(sub), sub.text), [navGating]);

  const isItemVisible = useCallback((item) => isEntryLicensed(item) && (!item.ownPermissionAny || holdsOwn(item.ownPermissionAny)) && navGating.allows(
    item.key, item.path,
    () => {
      if (isTenantAdmin) return true;
      if (item.permissionAny) return item.permissionAny.some(p => hasPermission(p));
      return !item.permission || hasPermission(item.permission);
    },
  ), [navGating, isTenantAdmin, hasPermission, holdsOwn, isEntryLicensed]);

  const isSubVisible = useCallback((sub) => isEntryLicensed(sub) && (!sub.ownPermissionAny || holdsOwn(sub.ownPermissionAny)) && navGating.allows(
    CHILD_NAV_KEYS.get(sub), sub.path,
    () => {
      if (isTenantAdmin) return true;
      // Same OR-gate the section entries have: an Employee Relations
      // complaint owner holds `er.own_complaint` and nothing else.
      if (sub.permissionAny) return sub.permissionAny.some((p) => hasPermission(p));
      return !sub.permission || hasPermission(sub.permission);
    },
  ), [navGating, isTenantAdmin, hasPermission, holdsOwn, isEntryLicensed]);

  // Register Web Push subscription (2 s after login, idempotent)
  useWebPush();

  // Called by NotificationDropdown when a VMS/FD notification arrives over the existing WebSocket
  const handleAppNotification = useCallback((n) => {
    const id = `${Date.now()}_${Math.random()}`;
    setAppToasts((prev) => [...prev.slice(-4), { id, title: n.title, message: n.message, appType: n.app_type, url: n.action_url }]);
    setAppNotifCount((c) => c + 1);
  }, []);

  const dismissToast = useCallback((id) => {
    setAppToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const isSupplierPortal = location.pathname.startsWith('/supplier-portal');

  // Tenant admins are no longer short-circuited past the filter: an entry an
  // administrator has set to HIDDEN is hidden from them too, or the setting
  // would be untestable by the person configuring it. `isItemVisible` still
  // returns true for an admin in every other case.
  const filteredSections = (isSupplierPortal ? supplierPortalSections : sidebarSections)
    .map(section => ({
      ...section,
      items: section.items.filter(isItemVisible),
    }))
    .filter(section => section.items.length > 0);

  // Flat list of all visible menu items for search and favourites lookup
  const allMenuItems = useMemo(() => {
    const items = [];
    filteredSections.forEach(section => {
      section.items.forEach(item => {
        items.push({ path: item.path, label: itemLabel(item), icon: item.icon, parent: null, external: false });
        if (item.submenus) {
          item.submenus
            .filter(isSubVisible)
            .forEach(sub => items.push({
              path: sub.path, label: subLabel(sub), icon: sub.icon, parent: itemLabel(item), external: sub.external || false, sso: sub.sso || false,
            }));
        }
        if (item.submenuGroups) {
          item.submenuGroups.forEach(group => {
            const leafItems = group.subGroups
              ? group.subGroups.flatMap(sg => sg.items || [])
              : (group.items || []);
            leafItems
              .filter(isSubVisible)
              .forEach(sub => items.push({
                path: sub.path, label: subLabel(sub), icon: sub.icon, parent: itemLabel(item), external: sub.external || false, sso: sub.sso || false,
              }));
          });
        }
      });
    });
    // De-duplicate by destination path so the same page (e.g. a parent and its
    // landing-page child both pointing at /mdm) appears only once in search/favourites.
    const seen = new Set();
    return items.filter(it => {
      if (seen.has(it.path)) return false;
      seen.add(it.path);
      return true;
    });
  }, [filteredSections, isSubVisible, itemLabel, subLabel]);

  // Filtered search results
  const searchResults = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return [];
    return allMenuItems.filter(item =>
      item.label.toLowerCase().includes(q) ||
      (item.parent && item.parent.toLowerCase().includes(q))
    );
  }, [menuSearch, allMenuItems]);

  // Record the visited page for the module launchers' "recently visited" list.
  // Only pages the menu actually names are recorded, so a detail URL is filed
  // under nothing rather than under a fabricated label. Client-side only — see
  // utils/recentPages.js.
  // `allMenuItems` takes a new identity on every render (filteredSections is
  // rebuilt inline), so the effect below fires on every render — the ref keeps
  // it to one write per actual navigation.
  const lastRecordedPath = useRef(null);
  useEffect(() => {
    if (lastRecordedPath.current === location.pathname) return;
    const match = allMenuItems.find(it => it.path === location.pathname);
    if (!match) return;
    lastRecordedPath.current = location.pathname;
    recordRecentPage(user?.id, { path: match.path, label: match.label });
  }, [location.pathname, allMenuItems, user?.id]);

  // Load favourites from API on mount
  useEffect(() => {
    api.get('/users/api/navigation-preferences/').then(res => {
      setFavorites(res.data.favorites || []);
    }).catch(() => {});
  }, []);

  const toggleFavorite = useCallback((e, item) => {
    e.stopPropagation();
    // Menu rows are real <a> elements (so the browser's own "open in new tab"
    // works) — the star must not follow the link.
    e.preventDefault();
    const isFav = favorites.some(f => f.path === item.path);
    const updated = isFav
      ? favorites.filter(f => f.path !== item.path)
      : [...favorites, { path: item.path, label: item.label, parent: item.parent || null, external: item.external || false, sso: item.sso || false }];
    setFavorites(updated);
    if (saveFavoritesTimer.current) clearTimeout(saveFavoritesTimer.current);
    saveFavoritesTimer.current = setTimeout(() => {
      api.post('/users/api/navigation-preferences/', { favorites: updated }).catch(() => {});
    }, 800);
  }, [favorites]);

  // Fetch unread notification count
  useEffect(() => {
    dispatch(fetchUnreadNotificationCount()).catch(() => {});
    const intervalId = setInterval(() => {
      dispatch(fetchUnreadNotificationCount()).catch(() => {});
    }, 60000);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  // Global Ctrl+K / Cmd+K opens the universal-search spotlight
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSpotlightOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Auto-expand the section containing the active route
  useEffect(() => {
    const path = location.pathname;
    for (const section of filteredSections) {
      for (const item of section.items) {
        if (item.submenus) {
          const isActive = path === item.path || item.submenus.some(s => path.startsWith(s.path));
          if (isActive) {
            setExpandedItems(prev => ({ ...prev, [item.key]: true }));
          }
        }
        if (item.submenuGroups) {
          const groupLeafItems = g => g.subGroups
            ? g.subGroups.flatMap(sg => sg.items || [])
            : (g.items || []);
          const isActive = path === item.path || item.submenuGroups.some(g => groupLeafItems(g).some(s => path.startsWith(s.path)));
          if (isActive) {
            setExpandedItems(prev => {
              const updates = { [item.key]: true };
              for (const g of item.submenuGroups) {
                if (groupLeafItems(g).some(s => path.startsWith(s.path))) {
                  updates[`${item.key}__${g.header}`] = true;
                  // Also expand matching subGroup
                  if (g.subGroups) {
                    for (const sg of g.subGroups) {
                      if ((sg.items || []).some(s => path.startsWith(s.path))) {
                        updates[`${item.key}__${g.header}__${sg.header}`] = true;
                      }
                    }
                  }
                }
              }
              return { ...prev, ...updates };
            });
          }
        }
      }
    }
  }, [location.pathname]);

  const handleToggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileDrawerOpen(prev => !prev);
    } else {
      setSidebarOpen(prev => !prev);
    }
  }, [isMobile]);

  const handleExpandToggle = useCallback((key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSSONavigate = useCallback(async (nextPath) => {
    try {
      const response = await api.get(`/support/api/sso-token/?next=${encodeURIComponent(nextPath)}`);
      const ssoUrl = response.data?.sso_url;
      if (ssoUrl) {
        window.open(ssoUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('SSO token generation failed:', err);
      // Fallback: open the page directly (may prompt Django login)
      window.open(`https://biztimize.com${nextPath}`, '_blank', 'noopener,noreferrer');
    }
    if (isMobile) setMobileDrawerOpen(false);
  }, [isMobile]);

  const handleNavigate = useCallback((path, external, sso) => {
    if (sso) {
      handleSSONavigate(path);
    } else if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
    if (isMobile) setMobileDrawerOpen(false);
  }, [navigate, isMobile, handleSSONavigate]);

  // ---------------------------------------------------------------------
  // Menu entries behave like real links: ctrl/⌘-click, middle-click and the
  // browser's own context menu all work, plus an in-app right-click menu.
  // ---------------------------------------------------------------------
  const [linkMenu, setLinkMenu] = useState(null);

  const absoluteUrl = useCallback((path) => (
    path?.startsWith('http') ? path : `${window.location.origin}${path}`
  ), []);

  const closeLinkMenu = useCallback(() => setLinkMenu(null), []);

  const handleLinkContextMenu = useCallback((e, target) => {
    e.preventDefault();
    e.stopPropagation();
    setLinkMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 4, ...target });
  }, []);

  /**
   * Props that turn a ListItemButton into a navigable link row.
   * `onActivate` keeps whatever the row already did on a plain left-click
   * (SPA navigate, expand a group, clear the search box…).
   */
  const menuLinkProps = useCallback(({ path, label, external, sso, disabled, onActivate }) => {
    // SSO rows mint a one-time token, so there is no URL to hand the browser.
    const href = !disabled && !sso && path && (external || path.startsWith('/')) ? path : null;
    const props = {
      onClick: (e) => {
        // A modified click on a real link is the browser's job, not ours.
        if (href && (e.ctrlKey || e.metaKey || e.shiftKey)) return;
        if (href) e.preventDefault();
        onActivate(e);
      },
    };
    if (href) {
      props.component = 'a';
      props.href = href;
      if (external) {
        props.target = '_blank';
        props.rel = 'noopener noreferrer';
      } else {
        props.onContextMenu = (e) => handleLinkContextMenu(e, { path, label });
      }
    }
    return props;
  }, [handleLinkContextMenu]);

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleCloseMenu();
    try {
      await dispatch(logout()).unwrap();
    } catch (e) {
      // proceed to login regardless
    }
    clearActiveCompany();
    navigate('/auth/login');
  };

  const isActive = (path) => location.pathname === path;

  const isItemActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.landingPath && location.pathname === item.landingPath) return true;
    if (item.submenus) {
      return item.submenus.some(s => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));
    }
    if (item.submenuGroups) {
      return item.submenuGroups.some(g => {
        const leaves = g.subGroups ? g.subGroups.flatMap(sg => sg.items || []) : (g.items || []);
        return leaves.some(s => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));
      });
    }
    return false;
  };

  const getTenantName = () => {
    // Prefer the full company name returned by /auth/me; fall back to a
    // title-cased subdomain when it isn't available yet.
    if (user?.tenant_name) return user.tenant_name;
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    return subdomain
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const currentSidebarWidth = isMobile ? SIDEBAR_WIDTH : (sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED);
  // On mobile/tablet the drawer is always full-width, so always show labels even when sidebarOpen is false
  const showLabels = sidebarOpen || isMobile || isTablet;

  const sidebarContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
      // Menu rows render as <a> so links work natively — keep them looking like rows.
      '& a': { textDecoration: 'none', color: 'inherit' },
    }}>
      {/* Menu search input — only when sidebar is expanded */}
      {showLabels && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <InputBase
            value={menuSearch}
            onChange={e => setMenuSearch(e.target.value)}
            placeholder="Search menus..."
            fullWidth
            startAdornment={<SearchIcon sx={{ fontSize: 15, color: 'text.disabled', mr: 0.75, flexShrink: 0 }} />}
            endAdornment={
              menuSearch ? (
                <IconButton size="small" onClick={() => setMenuSearch('')} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </IconButton>
              ) : null
            }
            sx={{
              fontSize: '0.82rem',
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              px: 1.25,
              py: 0.35,
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {/* Search results view */}
        {showLabels && menuSearch.trim() ? (
          searchResults.length > 0 ? (
            <List disablePadding sx={{ px: 0.5 }}>
              {searchResults.map((item) => {
                const isFav = favorites.some(f => f.path === item.path);
                const active = isActive(item.path);
                return (
                  <ListItem key={item.path} disablePadding
                    sx={{ '& .fav-star': { opacity: isFav ? 1 : 0 }, '&:hover .fav-star': { opacity: 1 } }}
                  >
                    <ListItemButton
                      {...menuLinkProps({
                        path: item.path,
                        label: item.label,
                        external: item.external,
                        sso: item.sso,
                        onActivate: () => { handleNavigate(item.path, item.external, item.sso); setMenuSearch(''); },
                      })}
                      sx={{
                        py: 0.5, px: 1.5, borderRadius: 1.5,
                        borderLeft: active ? '3px solid' : '3px solid transparent',
                        borderColor: active ? 'secondary.main' : 'transparent',
                        bgcolor: active ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                        '&:hover': { bgcolor: active ? 'rgba(52, 152, 219, 0.12)' : 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: active ? 'secondary.main' : 'text.secondary', '& .MuiSvgIcon-root': { fontSize: 18 } }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.parent}
                        primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: active ? 600 : 400, color: active ? 'secondary.main' : 'text.primary' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                      <IconButton
                        className="fav-star"
                        size="small"
                        onClick={e => toggleFavorite(e, item)}
                        sx={{ p: 0.25, flexShrink: 0, color: isFav ? 'warning.main' : 'text.disabled' }}
                      >
                        {isFav ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography sx={{ px: 2.5, py: 2, fontSize: '0.8rem', color: 'text.disabled', fontStyle: 'italic' }}>
              No menu found for "{menuSearch}"
            </Typography>
          )
        ) : (
          <>
            {/* Favourites section */}
            {showLabels && favorites.length > 0 && (
              <React.Fragment>
                <Typography variant="caption" sx={{ px: 2.5, pt: 1.5, pb: 0.25, display: 'block', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em', color: 'warning.main', textTransform: 'uppercase' }}>
                  Favourites
                </Typography>
                <List disablePadding sx={{ px: 0.5 }}>
                  {favorites.map((fav) => {
                    const menuItem = allMenuItems.find(m => m.path === fav.path);
                    const active = isActive(fav.path);
                    return (
                      <ListItem key={fav.path} disablePadding
                        sx={{ '& .fav-star': { opacity: 0 }, '&:hover .fav-star': { opacity: 1 } }}
                      >
                        <ListItemButton
                          {...menuLinkProps({
                            path: fav.path,
                            label: menuItem?.label || fav.label,
                            external: fav.external,
                            sso: fav.sso,
                            onActivate: () => handleNavigate(fav.path, fav.external, fav.sso),
                          })}
                          sx={{
                            py: 0.25, px: 1.5, borderRadius: 1.5,
                            borderLeft: active ? '3px solid' : '3px solid transparent',
                            borderColor: active ? 'secondary.main' : 'transparent',
                            bgcolor: active ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                            '&:hover': { bgcolor: active ? 'rgba(52, 152, 219, 0.12)' : 'action.hover' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: active ? 'secondary.main' : 'warning.main', '& .MuiSvgIcon-root': { fontSize: 18 } }}>
                            {menuItem?.icon || <StarIcon sx={{ fontSize: 18 }} />}
                          </ListItemIcon>
                          <ListItemText
                            primary={menuItem?.label || fav.label}
                            secondary={menuItem ? menuItem.parent : fav.parent}
                            primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: active ? 600 : 400, color: active ? 'secondary.main' : 'text.primary' }}
                            secondaryTypographyProps={{ fontSize: '0.7rem' }}
                          />
                          <IconButton
                            className="fav-star"
                            size="small"
                            onClick={e => toggleFavorite(e, fav)}
                            sx={{ p: 0.25, flexShrink: 0, color: 'warning.main' }}
                          >
                            <StarIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
                <Divider sx={{ mx: 1.5, my: 0.5 }} />
              </React.Fragment>
            )}

            {/* Normal sections */}
            {filteredSections.map((section) => (
          <React.Fragment key={section.id}>
            {section.header && showLabels && (
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.25,
                  display: 'block',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.1em',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                }}
              >
                {section.header}
              </Typography>
            )}
            {section.header && !showLabels && (
              <Box sx={{ mx: 1.5, my: 0.5, borderTop: '1px solid', borderColor: 'divider' }} />
            )}
            <List disablePadding sx={{ px: 0.5 }}>
              {section.items.map((item) => {
                const active = isItemActive(item);
                const expanded = expandedItems[item.key];
                const hasSubmenus = (item.submenus && item.submenus.length > 0) || (item.submenuGroups && item.submenuGroups.length > 0);
                const isItemFav = favorites.some(f => f.path === item.path);
                const itemFavData = { path: item.path, label: itemLabel(item), parent: null, external: false };

                return (
                  <React.Fragment key={item.key}>
                    <ListItem disablePadding
                      sx={showLabels ? { '& .fav-star': { opacity: isItemFav ? 1 : 0 }, '&:hover .fav-star': { opacity: 1 } } : {}}
                    >
                      <Tooltip title={!showLabels ? itemLabel(item) : ''} placement="right" arrow>
                        <ListItemButton
                          {...menuLinkProps({
                            // A group header expands rather than navigates, so it
                            // is deliberately not a link — UNLESS the item names a
                            // `landingPath` (a module launcher). Then it is a real
                            // link, so ctrl/middle-click opens it in a tab too.
                            path: hasSubmenus && showLabels
                              ? (item.landingPath || null)
                              : (item.landingPath || item.path),
                            label: itemLabel(item),
                            external: item.external,
                            sso: item.sso,
                            onActivate: () => {
                              if (hasSubmenus && showLabels) {
                                // Opening the group also opens its launcher;
                                // collapsing it is just a collapse, or clicking to
                                // close would navigate the user somewhere.
                                const wasExpanded = expanded;
                                handleExpandToggle(item.key);
                                if (item.landingPath && !wasExpanded) {
                                  handleNavigate(item.landingPath);
                                }
                              } else if (item.landingPath) {
                                // Collapsed rail: one click still lands on the
                                // launcher, since there is no tree to expand.
                                handleNavigate(item.landingPath);
                              } else {
                                handleNavigate(item.path, item.external, item.sso);
                              }
                            },
                          })}
                          sx={{
                            minHeight: 36,
                            py: 0.25,
                            px: showLabels ? 2 : 1.5,
                            borderRadius: 1.5,
                            justifyContent: showLabels ? 'initial' : 'center',
                            borderLeft: active ? '3px solid' : '3px solid transparent',
                            borderColor: active ? 'secondary.main' : 'transparent',
                            bgcolor: active ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                            '&:hover': {
                              bgcolor: active ? 'rgba(52, 152, 219, 0.12)' : 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: showLabels ? 2 : 0,
                              justifyContent: 'center',
                              color: active ? 'secondary.main' : 'text.secondary',
                              '& .MuiSvgIcon-root': { fontSize: 20 },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {showLabels && (
                            <>
                              <ListItemText
                                primary={itemLabel(item)}
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: active ? 600 : 400,
                                  color: active ? 'secondary.main' : 'text.primary',
                                }}
                              />
                              {hasSubmenus && (
                                expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
                              )}
                              <IconButton
                                className="fav-star"
                                size="small"
                                onClick={e => toggleFavorite(e, itemFavData)}
                                sx={{ p: 0.25, flexShrink: 0, color: isItemFav ? 'warning.main' : 'text.disabled' }}
                              >
                                {isItemFav ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </>
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>

                    {/* Flat submenu items */}
                    {item.submenus && item.submenus.length > 0 && showLabels && (
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List disablePadding>
                          {item.submenus.filter(isSubVisible).map((sub) => {
                            const subActive = isActive(sub.path);
                            const subIsFav = favorites.some(f => f.path === sub.path);
                            const subFavData = { path: sub.path, label: subLabel(sub), parent: itemLabel(item), external: sub.external || false, sso: sub.sso || false };
                            return (
                            <ListItem key={sub.text} disablePadding
                              sx={{ '& .fav-star': { opacity: subIsFav ? 1 : 0 }, '&:hover .fav-star': { opacity: 1 } }}
                            >
                              <ListItemButton
                                {...menuLinkProps({
                                  path: sub.path,
                                  label: subLabel(sub),
                                  external: sub.external,
                                  sso: sub.sso,
                                  disabled: sub.disabled,
                                  onActivate: () => !sub.disabled && handleNavigate(sub.path, sub.external, sub.sso),
                                })}
                                disabled={sub.disabled}
                                sx={{
                                  minHeight: 30,
                                  py: 0.125,
                                  pl: 6,
                                  pr: 1,
                                  borderRadius: 1,
                                  mx: 0.5,
                                  bgcolor: subActive ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                                  '&:hover': { bgcolor: sub.disabled ? 'transparent' : 'action.hover' },
                                  opacity: sub.disabled ? 0.45 : 1,
                                  cursor: sub.disabled ? 'default' : 'pointer',
                                }}
                              >
                                <ListItemText
                                  primary={subLabel(sub)}
                                  primaryTypographyProps={{
                                    fontSize: '0.8rem',
                                    fontWeight: subActive ? 600 : 400,
                                    color: sub.disabled ? 'text.disabled' : subActive ? 'secondary.main' : 'text.secondary',
                                  }}
                                />
                                <IconButton
                                  className="fav-star"
                                  size="small"
                                  onClick={e => toggleFavorite(e, subFavData)}
                                  sx={{ p: 0.25, flexShrink: 0, color: subIsFav ? 'warning.main' : 'text.disabled' }}
                                >
                                  {subIsFav ? <StarIcon sx={{ fontSize: 12 }} /> : <StarBorderIcon sx={{ fontSize: 12 }} />}
                                </IconButton>
                              </ListItemButton>
                            </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}

                    {/* Grouped submenu items */}
                    {item.submenuGroups && item.submenuGroups.length > 0 && showLabels && (
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List disablePadding>
                          {item.submenuGroups.map((group) => {
                            const groupKey = `${item.key}__${group.header}`;
                            const groupExpanded = expandedItems[groupKey];
                            const groupLeaves = group.subGroups
                              ? group.subGroups.flatMap(sg => sg.items || [])
                              : (group.items || []);
                            const groupHasActive = groupLeaves.some(s => isActive(s.path));
                            // A header with nothing under it is a dead end: hide
                            // the group when the user can open none of its pages.
                            if (!groupLeaves.some(isSubVisible)) return null;

                            const renderSubItem = (sub, pl = 6) => {
                              const subActive = isActive(sub.path);
                              const subIsFav = favorites.some(f => f.path === sub.path);
                              const subFavData = { path: sub.path, label: subLabel(sub), parent: itemLabel(item), external: sub.external || false, sso: sub.sso || false };
                              return (
                                <ListItem key={sub.text} disablePadding
                                  sx={{ '& .fav-star': { opacity: subIsFav ? 1 : 0 }, '&:hover .fav-star': { opacity: 1 } }}
                                >
                                  <ListItemButton
                                    {...menuLinkProps({
                                      path: sub.path,
                                      label: subLabel(sub),
                                      external: sub.external,
                                      sso: sub.sso,
                                      onActivate: () => handleNavigate(sub.path, sub.external, sub.sso),
                                    })}
                                    sx={{
                                      minHeight: 30,
                                      py: 0.125,
                                      pl,
                                      pr: 1,
                                      borderRadius: 1,
                                      mx: 0.5,
                                      bgcolor: subActive ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                  >
                                    <ListItemText
                                      primary={subLabel(sub)}
                                      primaryTypographyProps={{
                                        fontSize: '0.8rem',
                                        fontWeight: subActive ? 600 : 400,
                                        color: subActive ? 'secondary.main' : 'text.secondary',
                                      }}
                                    />
                                    <IconButton
                                      className="fav-star"
                                      size="small"
                                      onClick={e => toggleFavorite(e, subFavData)}
                                      sx={{ p: 0.25, flexShrink: 0, color: subIsFav ? 'warning.main' : 'text.disabled' }}
                                    >
                                      {subIsFav ? <StarIcon sx={{ fontSize: 12 }} /> : <StarBorderIcon sx={{ fontSize: 12 }} />}
                                    </IconButton>
                                  </ListItemButton>
                                </ListItem>
                              );
                            };

                            return (
                              <React.Fragment key={group.header}>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => handleExpandToggle(groupKey)}
                                    sx={{
                                      minHeight: 28,
                                      py: 0.25,
                                      pl: 4.5,
                                      pr: 2,
                                      borderRadius: 1,
                                      mx: 0.5,
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                  >
                                    <ListItemText
                                      primary={group.header}
                                      primaryTypographyProps={{
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        letterSpacing: '0.06em',
                                        color: groupHasActive ? 'secondary.main' : 'text.disabled',
                                        textTransform: 'uppercase',
                                      }}
                                    />
                                    {groupExpanded
                                      ? <ExpandLess sx={{ fontSize: 14, color: 'text.disabled' }} />
                                      : <ExpandMore sx={{ fontSize: 14, color: 'text.disabled' }} />
                                    }
                                  </ListItemButton>
                                </ListItem>
                                <Collapse in={groupExpanded} timeout="auto" unmountOnExit>
                                  {group.subGroups
                                    ? group.subGroups
                                        .filter(sg => isEntryLicensed(sg) && (isTenantAdmin || !sg.permission || hasPermission(sg.permission)))
                        .filter(sg => (sg.items || []).some(isSubVisible))
                                        .map(sg => {
                                          const sgKey = `${item.key}__${group.header}__${sg.header}`;
                                          const sgExpanded = expandedItems[sgKey];
                                          const sgHasActive = (sg.items || []).some(s => isActive(s.path));
                                          return (
                                            <React.Fragment key={sg.header}>
                                              <ListItem disablePadding>
                                                <ListItemButton
                                                  onClick={() => handleExpandToggle(sgKey)}
                                                  sx={{
                                                    minHeight: 26,
                                                    py: 0.2,
                                                    pl: 5.5,
                                                    pr: 2,
                                                    borderRadius: 1,
                                                    mx: 0.5,
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                  }}
                                                >
                                                  <ListItemText
                                                    primary={sg.header}
                                                    primaryTypographyProps={{
                                                      fontWeight: 600,
                                                      fontSize: '0.7rem',
                                                      letterSpacing: '0.04em',
                                                      color: sgHasActive ? 'secondary.main' : 'text.secondary',
                                                    }}
                                                  />
                                                  {sgExpanded
                                                    ? <ExpandLess sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                    : <ExpandMore sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                  }
                                                </ListItemButton>
                                              </ListItem>
                                              <Collapse in={sgExpanded} timeout="auto" unmountOnExit>
                                                {(sg.items || [])
                                                  .filter(isSubVisible)
                                                  .map(sub => renderSubItem(sub, 7))}
                                              </Collapse>
                                            </React.Fragment>
                                          );
                                        })
                                    : (group.items || [])
                                        .filter(isSubVisible)
                                        .map(sub => renderSubItem(sub, 6))
                                  }
                                </Collapse>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </React.Fragment>
        ))}
          </>
        )}
      </Box>
    </Box>
  );

  // Sticky-note colour config per app type
  const APP_TOAST_CONFIG = {
    vms: {
      bgcolor:    '#FFF8E1',  // amber-50
      border:     '3px solid #FFC107',
      headerBg:   '#FFC107',
      headerColor:'#212121',
      label:      navGating.labelFor('visitor-mgmt', 'Visitor Management'),
      emoji:      '🪪',
    },
    fd: {
      bgcolor:    '#E3F2FD',  // blue-50
      border:     '3px solid #1976D2',
      headerBg:   '#1976D2',
      headerColor:'#fff',
      label:      navGating.labelFor('front-desk', 'Front Desk'),
      emoji:      '📥',
    },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <ToastContainer position="top-right" newestOnTop autoClose={false} />

      {/* Sticky-note in-app toast stack for VMS / Front Desk */}
      {appToasts.length > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 24,
          right: 16,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxHeight: '80vh',
          overflowY: 'auto',
          overflowX: 'visible',
          pointerEvents: 'none',   // let clicks pass through gaps
          '& > *': { pointerEvents: 'all' },
        }}>
          {appToasts.map((toast) => {
            const cfg = APP_TOAST_CONFIG[toast.appType] || APP_TOAST_CONFIG.vms;
            return (
              <Box
                key={toast.id}
                sx={{
                  width: 340,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                  border: cfg.border,
                  bgcolor: cfg.bgcolor,
                  cursor: toast.url ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (toast.url) navigate(toast.url);
                  dismissToast(toast.id);
                }}
              >
                {/* Coloured header strip */}
                <Box sx={{
                  bgcolor: cfg.headerBg,
                  px: 2, py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <Typography sx={{ color: cfg.headerColor, fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>
                    {cfg.emoji} {cfg.label}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ color: cfg.headerColor, fontSize: 18, lineHeight: 1, cursor: 'pointer', ml: 1 }}
                    onClick={(e) => { e.stopPropagation(); dismissToast(toast.id); }}
                  >
                    ×
                  </Typography>
                </Box>
                {/* Body */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5, color: '#212121' }}>
                    {toast.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                    {toast.message}
                  </Typography>
                  {toast.url && (
                    <Typography sx={{ fontSize: 12, color: cfg.headerBg, mt: 1, fontWeight: 600 }}>
                      Tap to view →
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          bgcolor: '#2C3E50',
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important`, px: { xs: 1, sm: 2 } }}>
          {/* Hamburger + Logo */}
          <IconButton color="inherit" onClick={handleToggleSidebar} edge="start" sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.2rem' },
              letterSpacing: '-0.01em',
              color: '#fff',
              cursor: 'pointer',
              minWidth: 'fit-content',
            }}
            onClick={() => navigate('/')}
          >
            {getTenantName()}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Global Search — collapsed to an icon, expands on focus */}
            {searchExpanded ? (
              <HeaderSearchContainer>
                <GlobalSearchBar
                  placeholder="Search POs, Customers, Assets..."
                  size="small"
                  autoFocus
                  onSearch={(q) => navigate(`/search?q=${encodeURIComponent(q.trim())}`)}
                  onCollapse={() => setSearchExpanded(false)}
                />
              </HeaderSearchContainer>
            ) : (
              <Tooltip title="Search">
                <IconButton color="inherit" onClick={() => setSearchExpanded(true)} aria-label="Open search">
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notifications */}
            <IconButton
              color="inherit"
              onClick={(e) => {
                setNotifAnchorEl(e.currentTarget);
                setAppNotifCount(0); // clear VMS/FD badge when user opens dropdown
              }}
            >
              <Badge badgeContent={(unreadCount || 0) + appNotifCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <NotificationDropdown
              anchorEl={notifAnchorEl}
              onClose={() => setNotifAnchorEl(null)}
              onAppNotification={handleAppNotification}
            />

            {/* Offline Upload Queue */}
            <OfflineUploadStatus />

            {/* Biztimize Notes for the current screen (FR-81) */}
            <ScreenNotesButton />

            {/* Help Assistant */}
            <Tooltip title="Ask the Help Assistant">
              <IconButton color="inherit" onClick={() => setHelpOpen(true)} aria-label="Open Help Assistant">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            {/* The company this tab works in — see services/activeCompany.js */}
            <CompanySwitcher />

            {/* User */}
            <Box
              onClick={handleMenu}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'secondary.main',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  color: '#fff',
                  fontWeight: 500,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {user?.first_name || user?.username || 'User'}
              </Typography>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ mt: 1 }}
            >
              <MenuItem onClick={handleCloseMenu}>Profile</MenuItem>
              <MenuItem onClick={() => { handleCloseMenu(); navigate('/user-defaults/my'); }}>My Defaults</MenuItem>
              <MenuItem onClick={() => { handleCloseMenu(); navigate('/newsletter-subscription'); }}>Newsletter</MenuItem>
              <MenuItem onClick={() => { handleCloseMenu(); navigate('/security'); }}>Security</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Right-click menu for sidebar entries */}
      <Menu
        open={Boolean(linkMenu)}
        onClose={closeLinkMenu}
        anchorReference="anchorPosition"
        anchorPosition={linkMenu ? { top: linkMenu.mouseY, left: linkMenu.mouseX } : undefined}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            window.open(absoluteUrl(linkMenu.path), '_blank', 'noopener,noreferrer');
            closeLinkMenu();
          }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}><OpenInNewIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Open in new tab" primaryTypographyProps={{ fontSize: '0.82rem' }} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const url = absoluteUrl(linkMenu.path);
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(url).catch(() => {});
            }
            closeLinkMenu();
          }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}><ContentCopyIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Copy link address" primaryTypographyProps={{ fontSize: '0.82rem' }} />
        </MenuItem>
      </Menu>

      {/* Sidebar - Desktop (permanent) */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{
            width: currentSidebarWidth,
            flexShrink: 0,
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
          }}
        >
          <Box
            sx={{
              width: currentSidebarWidth,
              position: 'fixed',
              top: HEADER_HEIGHT,
              left: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
              overflowX: 'hidden',
              zIndex: theme.zIndex.drawer,
            }}
          >
            {sidebarContent}
          </Box>
        </Box>
      )}

      {/* Sidebar - Mobile (temporary drawer) */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              top: HEADER_HEIGHT,
              height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            },
          }}
        >
          {sidebarContent}
        </MuiDrawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: `${HEADER_HEIGHT}px`,
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          bgcolor: 'background.default',
          px: { xs: 2, sm: 3 },
          py: 3,
          overflow: 'auto',
        }}
      >
        <LicenseGraceBanner />
        <HotNewsBanner />
        {/* No screen renders until the tab's company is known, so its first
            request already carries X-Company-ID (the lock). An unreachable
            endpoint never blocks the app — the server then uses the default. */}
        {companyReady ? children : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        )}
      </Box>

      {/* AI Help Assistant (floating, RAG over HelpDoc) */}
      <HelpAssistantWidget open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Global Ctrl+K search spotlight */}
      <SearchSpotlightModal
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
      />
    </Box>
  );
};

/**
 * Marks this component as an application shell for `RouteGate` (RBAC Phase 3):
 * when the navigation registry refuses a route, the gate re-renders THIS
 * element with its refusal panel as children, so the user keeps the sidebar.
 * A static property rather than a name check — a production build minifies
 * component names away.
 */
ModernMainLayout.isAppLayout = true;

export default ModernMainLayout;
